import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { Phone, Mail, MapPin, Clock, Car, Users, Shield, Star, ChevronDown, ChevronRight, Check, Plane, Calendar, Menu, X, Globe, Award, Zap, Sun, Waves, Anchor, Mountain } from "lucide-react";

// Data imports are available in /app/frontend/src/data/ for future refactoring:
// - images.js: IMAGES, LOGO_URL
// - locations.js: LOCATIONS, TARIFFE, calculateDistance, getCityName
// - vehicles.js: getVehicles, vehicleDetails, galleryVehicles
// - translations.js: languages, translations, getT, getFaqData
// - bookingTranslations.js: bookingTranslations

// Hook per animazione count-up quando elemento entra in vista
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const elementRef = useRef(null);

  const startAnimation = useCallback(() => {
    if (hasStarted) return;
    setHasStarted(true);
    
    const startTime = performance.now();
    const startValue = 0;
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function: easeOutExpo per effetto più fluido
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      const currentValue = progress === 1 ? end : Math.floor(startValue + (end - startValue) * easeOutExpo);
      
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  useEffect(() => {
    if (!startOnView) {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted) {
            startAnimation();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [startAnimation, startOnView, hasStarted]);

  return { count, elementRef };
};

// Componente AnimatedNumber per riutilizzo
const AnimatedNumber = ({ value, suffix = "", prefix = "", duration = 2000, className = "" }) => {
  // Estrae il numero dalla stringa (es: "2.500+" -> 2500, "4.9/5" -> 4.9)
  const numericValue = parseFloat(value.toString().replace(/[^\d.]/g, ''));
  const isDecimal = value.toString().includes('.') && !value.toString().includes('.000');
  const hasThousandSeparator = value.toString().includes('.') && value.toString().split('.')[1]?.length === 3;
  
  const { count, elementRef } = useCountUp(
    hasThousandSeparator ? numericValue : numericValue,
    duration
  );

  // Formatta il numero con separatore delle migliaia italiano
  const formatNumber = (num) => {
    if (isDecimal && !hasThousandSeparator) {
      return num.toFixed(1);
    }
    if (hasThousandSeparator) {
      return num.toLocaleString('it-IT');
    }
    return num.toLocaleString('it-IT');
  };

  return (
    <span ref={elementRef} className={className}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
};

// Immagini veicoli - Auto di lusso NERE per NCC Alex Tours Transfer
const IMAGES = {
  // Hero - Maserati Quattroporte nera (dal sito originale)
  hero: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/41ea9b94c867aff83b66bea57644ae63e918b6fe7797dd89a66e1d5ecf63c67c.png",
  // About section - Mercedes Vito
  about: "https://images.pexels.com/photos/17455633/pexels-photo-17455633.jpeg?w=800",
  // Maserati Quattroporte nera (immagine originale)
  maserati: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/41ea9b94c867aff83b66bea57644ae63e918b6fe7797dd89a66e1d5ecf63c67c.png",
  maseratiGallery: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/bec2952c964d926b8c81e46df68026d3fc46a0223ce5562ed827da5a45d56510.png",
  // Mercedes Vito
  mercedesVito: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/720f6d6b1f69d5c1639235ea9eceacdd687b664bde874335a5048e23339849f9.png",
  // VW Caravelle
  vwCaravelle: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/9cacf618314dff7c9d4e4db19f4caded7b4fc1e4b07317e642c8d87ecb8a3c01.png",
  // Ford Tourneo Custom 2025 - FOTO REALE
  fordTourneo: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/nznlkefn_Ford%20Tourneo%20Custo%202025.png",
  fordTourneoOriginal: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/785b33531078ec3bff16e374a69fff8ad006494fe9324424a50688636ee4a790.png",
  // Opel Zafira Life - FOTO REALE
  opelZafira: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/v7yr8u87_Opel%20zafira%20Life.webp",
  opelZafiraOriginal: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/26dcf075d756086c63eb8b184f272f8ddf88698d96563cc4922143dc31203aba.png",
  // Mercedes Sprinter - FOTO REALE
  sprinter: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/s38adc16_sprinter%20fianco.jpg",
  // Servizi
  airport: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/nznlkefn_Ford%20Tourneo%20Custo%202025.png",
  chauffeur: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/720f6d6b1f69d5c1639235ea9eceacdd687b664bde874335a5048e23339849f9.png",
  // Cards flotta
  blackVan: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/720f6d6b1f69d5c1639235ea9eceacdd687b664bde874335a5048e23339849f9.png",
  luxurySedan: "https://static.prod-images.emergentagent.com/jobs/bd0ad86a-a192-4b3d-91d6-53b39c4dda6b/images/41ea9b94c867aff83b66bea57644ae63e918b6fe7797dd89a66e1d5ecf63c67c.png",
  minibus: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/s38adc16_sprinter%20fianco.jpg",
  // INTERNI VEICOLI - FOTO REALI SPRINTER
  interiorSprinter1: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/xls538mb_IMG-20250731-WA0033.jpg",
  interiorSprinter2: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/6lumcpck_IMG-20250731-WA0036.jpg",
  interiorSprinter3: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/pnse9wdd_IMG-20250731-WA0041.jpg",
  interiorSprinter4: "https://customer-assets.emergentagent.com/job_1d494fb4-03bd-426c-adc3-b88fad3b318b/artifacts/i1bq3kp5_IMG-20250731-WA0042.jpg",
};

// Logo ufficiale Alex Tours Transfer
const LOGO_URL = "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/0u5ry36j_logo%20per%20sfondo%203d.png";

// Language selector
const languages = [
  { code: 'IT', name: 'Italiano' },
  { code: 'EN', name: 'English' },
  { code: 'DE', name: 'Deutsch' },
  { code: 'FR', name: 'Français' },
  { code: 'ES', name: 'Español' },
];

// TRADUZIONI COMPLETE
const translations = {
  IT: {
    nav: { book: "Prenota Ora" },
    hero: {
      title: "Il Tuo Viaggio di Lusso",
      subtitle: "Inizia Qui",
      desc1: "Dall'alba al tramonto, siamo sempre al tuo fianco.",
      desc2: "Eleganza, puntualità e comfort in ogni trasferimento.",
      cta1: "Richiedi Preventivo",
      cta2: "Scopri la Flotta",
      available: "Disponibile 24/7",
      fleet: "Flotta di 6 veicoli",
      rates: "Tariffe trasparenti",
    },
    about: {
      badge: "OLTRE 10 ANNI DI ECCELLENZA",
      title1: "Il Tuo Tempo è Prezioso,",
      title2: "Noi lo Sappiamo",
      desc1: "Non siamo un semplice servizio di trasporto. Siamo il tuo partner di viaggio. Ogni dettaglio è curato per trasformare uno spostamento in un'esperienza di comfort assoluto.",
      desc2: "Dall'alba al tramonto, dai tuoi impegni più importanti ai momenti che contano davvero:",
      highlight: "noi ci siamo, sempre.",
      features: ["Autisti Certificati NCC", "Puntualità Garantita", "Veicoli Sempre Nuovi", "Servizio 5 Stelle"],
      years: "Anni di Esperienza",
      stat1: "2.500+",
      stat1Label: "Clienti Soddisfatti",
      stat2: "15.000+",
      stat2Label: "Trasferimenti",
      stat3: "4.9/5",
      stat3Label: "Rating Google",
      punctuality: "Puntualità",
    },
    services: {
      badge: "SERVIZI ESCLUSIVI",
      title: "Ogni Viaggio, Un'Esperienza",
      subtitle: "Non ci limitiamo a portarti a destinazione. Ti facciamo arrivare nel modo migliore.",
      airport: {
        badge: "PIÙ RICHIESTO",
        title: "Trasferimenti Aeroportuali",
        desc: "Il tuo volo non aspetta, noi nemmeno. Monitoraggio voli in tempo reale e autista sempre pronto.",
        features: ["Accoglienza con cartello nominativo", "Monitoraggio volo in tempo reale", "30 min di attesa gratuita"],
      },
      rental: {
        badge: "MASSIMA LIBERTÀ",
        title: "Noleggio con Conducente",
        desc: "Un autista dedicato, un veicolo di lusso, i tuoi tempi. Per meeting, eventi o giornate speciali.",
        features: ["80 km inclusi nelle prime 4h", "Totale libertà di itinerario", "Acqua a bordo"],
      },
    },
    fleet: {
      badge: "FLOTTA PREMIUM",
      title: "Solo il Meglio per Te",
      subtitle: "Veicoli di ultima generazione, sempre impeccabili",
      explore: "Scopri Ogni Dettaglio",
      exploreDesc: "Clicca su un veicolo per vedere interni e caratteristiche",
      details: "Scopri",
      passengers: "PASSEGGERI",
      upTo: "FINO A",
    },
    faq: {
      badge: "RISPOSTE RAPIDE",
      title: "Dubbi?",
      titleHighlight: "Chiariti!",
      subtitle: "Le risposte che cerchi, senza perdere tempo",
      notFound: "Altra domanda? Siamo qui per te.",
      contactBtn: "Scrivici una Email",
    },
    partnership: {
      badge: "DIVENTA PARTNER",
      title: "Cresciamo Insieme",
      hotel: {
        title: "Hotel & B&B",
        desc: "Offri ai tuoi ospiti un servizio transfer esclusivo e zero pensieri.",
        features: ["Tariffe riservate fino al -20%", "Co-branding disponibile"],
      },
      agency: {
        title: "Agenzie & Tour Operator",
        desc: "Integra il nostro servizio nei tuoi pacchetti. Qualità garantita, clienti felici.",
        features: ["Preventivi gruppi in 24h", "Pagamento 30gg", "Account manager dedicato"],
      },
      cta: "Vuoi saperne di più?",
      ctaDesc: "Inviaci una mail e riceverai una proposta personalizzata entro 24 ore.",
      email: "Richiedi Partnership",
      call: "Chiamaci",
    },
    summer: {
      badge: "ESTATE 2026",
      title: "Le Tue Destinazioni Estive",
      subtitle: "Raggiungi in totale comfort le mete più belle del Nord Italia. Transfer privato con autista, senza stress e senza pensieri.",
      destinations: [
        { name: "I Laghi", desc: "Lago di Como, Maggiore e d'Orta: eleganza, ville storiche e relax a un transfer di distanza." },
        { name: "Venezia", desc: "La città sull'acqua ti aspetta. Ti accompagniamo fino a Tronchetto o Piazzale Roma con la massima puntualità. Possibilità di tappa a Verona lungo il tragitto." },
        { name: "Genova & Liguria", desc: "Mare, Cinque Terre e Portofino: vivi la Riviera in comodità, sicurezza e stile." },
        { name: "La Montagna", desc: "Valle d'Aosta e Alpi: Courmayeur, Cervinia e il Monte Bianco. Aria fresca e panorami mozzafiato, in tutta comodità." },
      ],
      cta: "Prenota il Tuo Transfer Estivo",
      chooseLabel: "Scegli la destinazione",
    },
    comeFunziona: {
      badge: "SEMPLICE E VELOCE",
      title: "Come Funziona",
      subtitle: "Prenotare il tuo transfer è facile: in tre semplici passi ricevi il tuo preventivo gratuito.",
      steps: [
        { title: "Scegli il Servizio", desc: "Seleziona destinazione, veicolo e data del tuo viaggio." },
        { title: "Invia la Richiesta", desc: "Inviaci i dettagli con un click via email." },
        { title: "Ricevi il Preventivo", desc: "Ti rispondiamo al più presto con un preventivo gratuito e personalizzato." },
      ],
      cta: "Richiedi Preventivo Gratuito",
    },
    cta: {
      available: "veicoli disponibili oggi",
      title: "Non Rimandare,",
      titleHighlight: "Prenota Ora",
      subtitle: "Eleganza, comfort e puntualità in ogni trasferimento. Prenota il tuo viaggio in tutta tranquillità.",
      button: "Ottieni Preventivo Gratuito",
      features: ["✓ Preventivo Gratuito", "✓ Servizio 24/7"],
      stats: { clients: "Clienti Felici", vehicles: "Veicoli Top", available: "Sempre Attivi", rating: "su Google" },
    },
    testimonials: {
      badge: "DICONO DI NOI",
      title: "La Fiducia dei Nostri Clienti",
      items: [
        { quote: "Matrimonio da sogno! Maserati per noi sposi e minibus per gli invitati, tutto curato nei minimi dettagli. Servizio impeccabile, ospiti entusiasti.", name: "Giulia & Marco", role: "Sposi, Giugno 2025" },
        { quote: "Affidabili e sempre puntuali per i miei spostamenti di lavoro verso gli aeroporti. Auto impeccabili e autisti professionali. Un partner su cui contare.", name: "Stefano R.", role: "Imprenditore, Varese" },
        { quote: "Transfer per Malpensa alle 4 del mattino senza il minimo stress: autista già pronto ad attendermi. Viaggio comodo e rilassante, cinque stelle.", name: "Federica B.", role: "Manager, Como" },
        { quote: "Giornata sul Lago di Como con tappa a Bellagio: organizzazione perfetta, mezzo comodissimo e autista gentilissimo. Esperienza estiva indimenticabile!", name: "Roberto e Anna", role: "Turisti, Agosto 2025" },
      ],
    },
    footer: {
      quickQuote: "Preferisci scriverci?",
      quickQuoteDesc: "Email attiva 24/7 - Rispondiamo al più presto",
      contactBtn: "Scrivici Ora",
      description: "Servizio NCC di lusso per trasferimenti aeroportuali e noleggio con conducente in Lombardia, Piemonte e Valle d'Aosta.",
      contacts: "Contatti",
      bookingLabel: "Per prenotazioni scrivete a:",
      legalOffice: "Sede Legale",
    },
    cookie: {
      title: "Utilizziamo i cookie",
      desc: "Per offrirti la migliore esperienza di navigazione.",
      reject: "Rifiuta",
      accept: "Accetta",
      privacy: "Privacy Policy",
    },
    wedding: {
      badge: "MATRIMONI & CERIMONIE",
      title: "Il Giorno Più Bello",
      titleHighlight: "Merita il Meglio",
      subtitle: "Trasformiamo il vostro giorno speciale in un'esperienza indimenticabile, dall'arrivo in chiesa fino all'ultimo brindisi.",
      sposi: {
        title: "Trasporto Sposi",
        desc: "La Maserati Quattroporte per un arrivo da sogno. Eleganza italiana per il giorno più importante.",
        features: ["Auto decorata a tema", "Champagne a bordo", "Autista in livrea"],
      },
      invitati: {
        title: "Trasporto Invitati",
        desc: "Minivan e Minibus VIP per tutti i vostri ospiti. Nessuno resta indietro, tutti arrivano insieme.",
        features: ["Fino a 20 passeggeri per veicolo", "Coordinamento orari", "Servizio navetta"],
      },
      kit: {
        title: "Welcome Kit Personalizzato",
        desc: "Plaid con le vostre iniziali, lucine soffuse, champagne fresco. Ogni dettaglio curato per stupire.",
        features: ["Plaid personalizzati", "Lucine romantiche", "Snack e bevande premium"],
      },
      gallery: "I Nostri Allestimenti",
      cta: "Richiedi Preventivo Matrimonio",
      ctaDesc: "Raccontaci il vostro giorno speciale e creeremo il servizio perfetto per voi.",
    },
  },
  EN: {
    nav: { book: "Book" },
    hero: {
      title: "Your Luxury Journey",
      subtitle: "Starts Here",
      desc1: "Professional driver · Luxury vehicles",
      desc2: "Elegance, punctuality and comfort in every transfer.",
      cta1: "Request Quote",
      cta2: "Discover Fleet",
      available: "Available 24/7",
      fleet: "Fleet of 6 vehicles",
      rates: "Transparent rates",
    },
    about: {
      badge: "ABOUT US",
      title1: "Your Safety,",
      title2: "Our Priority",
      desc1: "For over 10 years, Alex Tours Transfer has been synonymous with excellence in private transportation. Every trip is carefully crafted: from punctuality to courtesy, from impeccable vehicle cleanliness to the professionalism of our drivers.",
      desc2: "Whether it's an airport transfer, a business trip, or a special occasion, our goal is one:",
      highlight: "to make you feel at home, wherever you go.",
      features: ["Certified Drivers", "Always Punctual", "Impeccable Vehicles", "Personalized Service"],
      years: "Years of Experience",
      stat1Label: "Satisfied Clients",
      stat2Label: "Transfers",
      stat3Label: "Google Rating",
      punctuality: "Punctuality",
    },
    services: {
      badge: "OUR SERVICES",
      title: "Tailored Solutions",
      subtitle: "Exclusive services designed to meet every travel need",
      airport: {
        badge: "PREMIUM",
        title: "Airport Transfers",
        desc: "Reliable service to and from all airports, ports, and train stations.",
        features: ["30 min free waiting", "Real-time flight monitoring", "Meet & Greet with name sign"],
      },
      rental: {
        badge: "FLEXIBLE",
        title: "Hourly Rental",
        desc: "Driver and vehicle at your complete disposal for as long as you need.",
        features: ["Minimum 4 hours, 80 km included", "Total itinerary flexibility", "Professional driver"],
      },
    },
    fleet: {
      badge: "OUR FLEET",
      title: "Premium Vehicles",
      subtitle: "Modern, comfortable, and perfectly maintained",
      explore: "Explore Our Vehicles",
      exploreDesc: "Click on a vehicle to discover all details",
      details: "Details",
      passengers: "PASSENGERS",
      upTo: "UP TO",
    },
    faq: {
      badge: "FAQ",
      title: "Have Any",
      titleHighlight: "Questions?",
      subtitle: "Here are answers to the most common questions",
      notFound: "Didn't find the answer you were looking for?",
      contactBtn: "Write us an Email",
    },
    partnership: {
      badge: "B2B PARTNERSHIP",
      title: "Partner With Us",
      hotel: {
        title: "Hotels & Accommodations",
        desc: "Offer your guests an exclusive transfer service to and from airports. Increase customer satisfaction and the perceived value of your property.",
        features: ["Reserved contracted rates", "Premium service with your brand"],
      },
      agency: {
        title: "Travel Agencies & Tour Operators",
        desc: "Integrate our NCC service into your tour packages. Guarantee your customers punctual and comfortable transfers throughout Italy.",
        features: ["Dedicated group quotes", "Monthly deferred billing", "Priority support 7/7"],
      },
      cta: "Shall we start collaborating?",
      ctaDesc: "Contact us to discuss a customized partnership. We'll respond within 24 hours with a tailored proposal for your needs.",
      email: "Write for Partnership",
      call: "Call Us",
    },
    summer: {
      badge: "SUMMER 2026",
      title: "Your Summer Destinations",
      subtitle: "Reach the most beautiful spots in Northern Italy in total comfort. Private transfer with driver, stress-free.",
      destinations: [
        { name: "The Lakes", desc: "Lake Como, Maggiore and Orta: elegance, historic villas and relaxation just a transfer away." },
        { name: "Venice", desc: "The city on water awaits. We take you to Tronchetto or Piazzale Roma with the utmost punctuality. Optional stop in Verona along the way." },
        { name: "Genoa & Liguria", desc: "Sea, Cinque Terre and Portofino: experience the Riviera in comfort, safety and style." },
        { name: "The Mountains", desc: "Aosta Valley and the Alps: Courmayeur, Cervinia and Mont Blanc. Fresh air and breathtaking views, in total comfort." },
      ],
      cta: "Book Your Summer Transfer",
      chooseLabel: "Choose destination",
    },
    comeFunziona: {
      badge: "SIMPLE & FAST",
      title: "How It Works",
      subtitle: "Booking your transfer is easy: get your free quote in three simple steps.",
      steps: [
        { title: "Choose the Service", desc: "Select destination, vehicle and date of your trip." },
        { title: "Send the Request", desc: "Send us the details in one click via email." },
        { title: "Get Your Quote", desc: "We reply as soon as possible with a free, personalized quote." },
      ],
      cta: "Request Free Quote",
    },
    cta: {
      available: "vehicles available this week",
      title: "Your Journey Awaits",
      subtitle: "Get a free quote in less than 2 minutes. No commitment, no surprises.",
      button: "Request Free Quote",
      features: ["Zero Hidden Costs", "Flexible Cancellation"],
      stats: { clients: "Satisfied Clients", vehicles: "Premium Vehicles", available: "Always Available", rating: "Google" },
    },
    testimonial: {
      quote: "Perfect wedding transport! Maserati for us and minibus for our guests. Everyone was amazed. Highly recommended!",
      role: "Newlyweds, Lake Como 2025",
    },
    footer: {
      quickQuote: "Prefer to write to us?",
      quickQuoteDesc: "Email active 24/7 - We reply as soon as possible",
      contactBtn: "Email Us",
      description: "Luxury NCC service for airport transfers and chauffeur hire in Lombardy, Piedmont and Aosta Valley.",
      contacts: "Contacts",
      bookingLabel: "For bookings write to:",
      legalOffice: "Registered Office",
    },
    cookie: {
      title: "We use cookies",
      desc: "This site uses cookies to improve your browsing experience.",
      reject: "Reject",
      accept: "Accept",
    },
    wedding: {
      badge: "WEDDINGS & CEREMONIES",
      title: "Your Special Day",
      titleHighlight: "Deserves the Best",
      subtitle: "We transform your special day into an unforgettable experience, from the church arrival to the last toast.",
      sposi: {
        title: "Bride & Groom Transport",
        desc: "The Maserati Quattroporte for a dream arrival. Italian elegance for your most important day.",
        features: ["Themed car decoration", "Champagne on board", "Chauffeur in livery"],
      },
      invitati: {
        title: "Guest Transport",
        desc: "Minivan and VIP Minibus for all your guests. No one left behind, everyone arrives together.",
        features: ["Up to 20 passengers per vehicle", "Schedule coordination", "Shuttle service"],
      },
      kit: {
        title: "Personalized Welcome Kit",
        desc: "Blankets with your initials, soft fairy lights, fresh champagne. Every detail crafted to amaze.",
        features: ["Personalized blankets", "Romantic fairy lights", "Premium snacks & drinks"],
      },
      gallery: "Our Setups",
      cta: "Request Wedding Quote",
      ctaDesc: "Tell us about your special day and we'll create the perfect service for you.",
    },
  },
  DE: {
    nav: { book: "Buchen" },
    hero: {
      title: "Ihre Luxusreise",
      subtitle: "Beginnt Hier",
      desc1: "Professioneller Fahrer · Luxusfahrzeuge",
      desc2: "Eleganz, Pünktlichkeit und Komfort bei jedem Transfer.",
      cta1: "Angebot Anfordern",
      cta2: "Flotte Entdecken",
      available: "24/7 Verfügbar",
      fleet: "Flotte von 6 Fahrzeugen",
      rates: "Transparente Preise",
    },
    about: {
      badge: "ÜBER UNS",
      title1: "Ihre Sicherheit,",
      title2: "Unsere Priorität",
      desc1: "Seit über 10 Jahren steht Alex Tours Transfer für Exzellenz im privaten Transport. Jede Fahrt wird sorgfältig geplant: von der Pünktlichkeit bis zur Höflichkeit, von der tadellosen Fahrzeugreinigung bis zur Professionalität unserer Fahrer.",
      desc2: "Ob Flughafentransfer, Geschäftsreise oder besonderer Anlass, unser Ziel ist eines:",
      highlight: "dass Sie sich überall wie zu Hause fühlen.",
      features: ["Zertifizierte Fahrer", "Immer Pünktlich", "Tadellose Fahrzeuge", "Persönlicher Service"],
      years: "Jahre Erfahrung",
      stat1Label: "Zufriedene Kunden",
      stat2Label: "Transfers",
      stat3Label: "Google Bewertung",
      punctuality: "Pünktlichkeit",
    },
    services: {
      badge: "UNSERE SERVICES",
      title: "Maßgeschneiderte Lösungen",
      subtitle: "Exklusive Dienstleistungen für alle Ihre Reisebedürfnisse",
      airport: {
        badge: "PREMIUM",
        title: "Flughafentransfers",
        desc: "Zuverlässiger Service zu und von allen Flughäfen, Häfen und Bahnhöfen.",
        features: ["30 Min. kostenlose Wartezeit", "Echtzeit-Flugüberwachung", "Meet & Greet mit Namensschild"],
      },
      rental: {
        badge: "FLEXIBEL",
        title: "Stundenmietung",
        desc: "Fahrer und Fahrzeug stehen Ihnen zur vollen Verfügung, solange Sie möchten.",
        features: ["Mindestens 4 Stunden, 80 km inklusive", "Volle Flexibilität bei der Route", "Professioneller Fahrer"],
      },
    },
    fleet: {
      badge: "UNSERE FLOTTE",
      title: "Premium Fahrzeuge",
      subtitle: "Modern, komfortabel und perfekt gewartet",
      explore: "Entdecken Sie Unsere Fahrzeuge",
      exploreDesc: "Klicken Sie auf ein Fahrzeug für alle Details",
      details: "Details",
      passengers: "PASSAGIERE",
      upTo: "BIS ZU",
    },
    faq: {
      badge: "HÄUFIGE FRAGEN",
      title: "Haben Sie",
      titleHighlight: "Fragen?",
      subtitle: "Hier sind Antworten auf die häufigsten Fragen",
      notFound: "Ihre Frage nicht gefunden?",
      contactBtn: "Schreiben Sie uns eine E-Mail",
    },
    partnership: {
      badge: "B2B PARTNERSCHAFT",
      title: "Partnern Sie Mit Uns",
      hotel: {
        title: "Hotels & Unterkünfte",
        desc: "Bieten Sie Ihren Gästen einen exklusiven Transferservice zu und von Flughäfen. Steigern Sie die Kundenzufriedenheit und den wahrgenommenen Wert Ihrer Unterkunft.",
        features: ["Reservierte Vertragspreise", "Premium-Service mit Ihrer Marke"],
      },
      agency: {
        title: "Reisebüros & Reiseveranstalter",
        desc: "Integrieren Sie unseren NCC-Service in Ihre Tourpakete. Garantieren Sie Ihren Kunden pünktliche und komfortable Transfers in ganz Italien.",
        features: ["Dedizierte Gruppenangebote", "Monatliche Nachzahlung", "Prioritärer Support 7/7"],
      },
      cta: "Sollen wir zusammenarbeiten?",
      ctaDesc: "Kontaktieren Sie uns für eine maßgeschneiderte Partnerschaft. Wir antworten innerhalb von 24 Stunden.",
      email: "Für Partnerschaft Schreiben",
      call: "Rufen Sie Uns An",
    },
    summer: {
      badge: "SOMMER 2026",
      title: "Ihre Sommerziele",
      subtitle: "Erreichen Sie die schönsten Orte Norditaliens in voller Bequemlichkeit. Privattransfer mit Fahrer, ganz ohne Stress.",
      destinations: [
        { name: "Die Seen", desc: "Comer See, Lago Maggiore und Ortasee: Eleganz, historische Villen und Erholung nur einen Transfer entfernt." },
        { name: "Venedig", desc: "Die Stadt auf dem Wasser wartet. Wir bringen Sie pünktlich bis Tronchetto oder Piazzale Roma. Optionaler Zwischenstopp in Verona unterwegs." },
        { name: "Genua & Ligurien", desc: "Meer, Cinque Terre und Portofino: erleben Sie die Riviera bequem, sicher und stilvoll." },
        { name: "Die Berge", desc: "Aostatal und Alpen: Courmayeur, Cervinia und der Mont Blanc. Frische Luft und atemberaubende Ausblicke, ganz bequem." },
      ],
      cta: "Buchen Sie Ihren Sommertransfer",
      chooseLabel: "Ziel wählen",
    },
    comeFunziona: {
      badge: "EINFACH & SCHNELL",
      title: "So Funktioniert's",
      subtitle: "Ihren Transfer zu buchen ist einfach: Erhalten Sie Ihr kostenloses Angebot in drei Schritten.",
      steps: [
        { title: "Service Wählen", desc: "Wählen Sie Ziel, Fahrzeug und Datum Ihrer Reise." },
        { title: "Anfrage Senden", desc: "Senden Sie uns die Details mit einem Klick per E-Mail." },
        { title: "Angebot Erhalten", desc: "Wir antworten so schnell wie möglich mit einem kostenlosen, persönlichen Angebot." },
      ],
      cta: "Kostenloses Angebot Anfordern",
    },
    cta: {
      available: "Fahrzeuge diese Woche verfügbar",
      title: "Ihre Reise Wartet",
      subtitle: "Eleganz, Komfort und Pünktlichkeit bei jedem Transfer. Buchen Sie Ihre Reise ganz entspannt.",
      button: "Kostenloses Angebot Anfordern",
      features: ["Keine Versteckten Kosten", "Service 24/7"],
      stats: { clients: "Zufriedene Kunden", vehicles: "Premium Fahrzeuge", available: "Immer Verfügbar", rating: "Google" },
    },
    testimonial: {
      quote: "Perfekter Hochzeitstransport! Maserati für uns und Minibus für unsere Gäste. Alle waren begeistert. Sehr empfehlenswert!",
      role: "Frischvermählte, Comer See 2025",
    },
    footer: {
      quickQuote: "Möchten Sie uns schreiben?",
      quickQuoteDesc: "E-Mail rund um die Uhr - Wir antworten so schnell wie möglich",
      contactBtn: "Schreiben Sie Uns",
    },
    cookie: {
      title: "Wir verwenden Cookies",
      desc: "Diese Website verwendet Cookies, um Ihre Browsererfahrung zu verbessern.",
      reject: "Ablehnen",
      accept: "Akzeptieren",
    },
    wedding: {
      badge: "HOCHZEITEN & ZEREMONIEN",
      title: "Ihr Besonderer Tag",
      titleHighlight: "Verdient das Beste",
      subtitle: "Wir verwandeln Ihren besonderen Tag in ein unvergessliches Erlebnis, von der Ankunft in der Kirche bis zum letzten Toast.",
      sposi: {
        title: "Brautpaar-Transport",
        desc: "Der Maserati Quattroporte für eine traumhafte Ankunft. Italienische Eleganz für Ihren wichtigsten Tag.",
        features: ["Thematische Autodekoration", "Champagner an Bord", "Chauffeur in Livree"],
      },
      invitati: {
        title: "Gästetransport",
        desc: "Minivan und VIP Minibus für alle Ihre Gäste. Niemand bleibt zurück, alle kommen zusammen an.",
        features: ["Bis zu 20 Passagiere pro Fahrzeug", "Terminkoordination", "Shuttle-Service"],
      },
      kit: {
        title: "Personalisiertes Welcome Kit",
        desc: "Decken mit Ihren Initialen, sanfte Lichterketten, frischer Champagner. Jedes Detail sorgfältig gestaltet.",
        features: ["Personalisierte Decken", "Romantische Lichterketten", "Premium Snacks & Getränke"],
      },
      gallery: "Unsere Ausstattungen",
      cta: "Hochzeitsangebot Anfordern",
      ctaDesc: "Erzählen Sie uns von Ihrem besonderen Tag und wir erstellen den perfekten Service für Sie.",
    },
  },
  FR: {
    nav: { book: "Réserver" },
    hero: {
      title: "Votre Voyage de Luxe",
      subtitle: "Commence Ici",
      desc1: "Chauffeur professionnel · Véhicules de luxe",
      desc2: "Élégance, ponctualité et confort à chaque transfert.",
      cta1: "Demander un Devis",
      cta2: "Découvrir la Flotte",
      available: "Disponible 24/7",
      fleet: "Flotte de 6 véhicules",
      rates: "Tarifs transparents",
    },
    about: {
      badge: "À PROPOS",
      title1: "Votre Sécurité,",
      title2: "Notre Priorité",
      desc1: "Depuis plus de 10 ans, Alex Tours Transfer est synonyme d'excellence dans le transport privé. Chaque voyage est soigneusement préparé : de la ponctualité à la courtoisie, de la propreté impeccable des véhicules au professionnalisme de nos chauffeurs.",
      desc2: "Qu'il s'agisse d'un transfert aéroport, d'un voyage d'affaires ou d'une occasion spéciale, notre objectif est unique :",
      highlight: "vous faire sentir chez vous, où que vous alliez.",
      features: ["Chauffeurs Certifiés", "Toujours Ponctuels", "Véhicules Impeccables", "Service Personnalisé"],
      years: "Années d'Expérience",
      stat1Label: "Clients Satisfaits",
      stat2Label: "Transferts",
      stat3Label: "Note Google",
      punctuality: "Ponctualité",
    },
    services: {
      badge: "NOS SERVICES",
      title: "Solutions Sur Mesure",
      subtitle: "Services exclusifs conçus pour répondre à tous vos besoins de voyage",
      airport: {
        badge: "PREMIUM",
        title: "Transferts Aéroport",
        desc: "Service fiable vers et depuis tous les aéroports, ports et gares.",
        features: ["30 min d'attente gratuite", "Suivi des vols en temps réel", "Meet & Greet avec pancarte nominative"],
      },
      rental: {
        badge: "FLEXIBLE",
        title: "Location à l'Heure",
        desc: "Chauffeur et véhicule à votre entière disposition pour le temps nécessaire.",
        features: ["Minimum 4 heures, 80 km inclus", "Flexibilité totale de l'itinéraire", "Chauffeur professionnel"],
      },
    },
    fleet: {
      badge: "NOTRE FLOTTE",
      title: "Véhicules Premium",
      subtitle: "Modernes, confortables et parfaitement entretenus",
      explore: "Explorez Nos Véhicules",
      exploreDesc: "Cliquez sur un véhicule pour découvrir tous les détails",
      details: "Détails",
      passengers: "PASSAGERS",
      upTo: "JUSQU'À",
    },
    faq: {
      badge: "FAQ",
      title: "Avez-vous des",
      titleHighlight: "Questions?",
      subtitle: "Voici les réponses aux questions les plus fréquentes",
      notFound: "Vous n'avez pas trouvé votre réponse?",
      contactBtn: "Écrivez-nous un Email",
    },
    partnership: {
      badge: "PARTENARIAT B2B",
      title: "Partenaires Avec Nous",
      hotel: {
        title: "Hôtels & Hébergements",
        desc: "Offrez à vos clients un service de transfert exclusif vers et depuis les aéroports. Augmentez la satisfaction client et la valeur perçue de votre établissement.",
        features: ["Tarifs contractuels réservés", "Service premium avec votre marque"],
      },
      agency: {
        title: "Agences de Voyage & Tour Opérateurs",
        desc: "Intégrez notre service NCC dans vos forfaits touristiques. Garantissez à vos clients des transferts ponctuels et confortables dans toute l'Italie.",
        features: ["Devis dédiés pour groupes", "Facturation mensuelle différée", "Support prioritaire 7/7"],
      },
      cta: "On commence à collaborer?",
      ctaDesc: "Contactez-nous pour discuter d'un partenariat personnalisé. Nous répondrons dans les 24 heures.",
      email: "Écrire pour Partenariat",
      call: "Appelez-nous",
    },
    summer: {
      badge: "ÉTÉ 2026",
      title: "Vos Destinations d'Été",
      subtitle: "Rejoignez les plus beaux endroits du Nord de l'Italie en tout confort. Transfert privé avec chauffeur, sans stress.",
      destinations: [
        { name: "Les Lacs", desc: "Lac de Côme, Majeur et d'Orta : élégance, villas historiques et détente à un transfert de distance." },
        { name: "Venise", desc: "La ville sur l'eau vous attend. Nous vous emmenons jusqu'au Tronchetto ou Piazzale Roma en toute ponctualité. Possibilité d'une halte à Vérone en chemin." },
        { name: "Gênes & Ligurie", desc: "Mer, Cinque Terre et Portofino : vivez la Riviera en confort, sécurité et style." },
        { name: "La Montagne", desc: "Vallée d'Aoste et Alpes : Courmayeur, Cervinia et le Mont Blanc. Air frais et panoramas à couper le souffle, tout confort." },
      ],
      cta: "Réservez Votre Transfert d'Été",
      chooseLabel: "Choisir la destination",
    },
    comeFunziona: {
      badge: "SIMPLE & RAPIDE",
      title: "Comment Ça Marche",
      subtitle: "Réserver votre transfert est facile : obtenez votre devis gratuit en trois étapes simples.",
      steps: [
        { title: "Choisissez le Service", desc: "Sélectionnez la destination, le véhicule et la date de votre voyage." },
        { title: "Envoyez la Demande", desc: "Envoyez-nous les détails en un clic par email." },
        { title: "Recevez le Devis", desc: "Nous répondons dès que possible avec un devis gratuit et personnalisé." },
      ],
      cta: "Demander un Devis Gratuit",
    },
    cta: {
      available: "véhicules disponibles cette semaine",
      title: "Votre Voyage Vous Attend",
      subtitle: "Élégance, confort et ponctualité à chaque transfert. Réservez votre voyage en toute sérénité.",
      button: "Demander un Devis Gratuit",
      features: ["Zéro Frais Cachés", "Service 24/7"],
      stats: { clients: "Clients Satisfaits", vehicles: "Véhicules Premium", available: "Toujours Disponibles", rating: "Google" },
    },
    testimonial: {
      quote: "Transport de mariage parfait! Maserati pour nous et minibus pour nos invités. Tout le monde était émerveillé. Fortement recommandé!",
      role: "Jeunes Mariés, Lac de Côme 2025",
    },
    footer: {
      quickQuote: "Vous préférez nous écrire ?",
      quickQuoteDesc: "Email actif 24/7 - Nous répondons dès que possible",
      contactBtn: "Écrivez-nous",
      description: "Service NCC de luxe pour transferts aéroport et location avec chauffeur en Lombardie, Piémont et Vallée d'Aoste.",
      contacts: "Contacts",
      bookingLabel: "Pour les réservations, écrivez à :",
      legalOffice: "Siège Social",
    },
    cookie: {
      title: "Nous utilisons des cookies",
      desc: "Ce site utilise des cookies pour améliorer votre expérience de navigation.",
      reject: "Refuser",
      accept: "Accepter",
    },
    wedding: {
      badge: "MARIAGES & CÉRÉMONIES",
      title: "Votre Jour Spécial",
      titleHighlight: "Mérite le Meilleur",
      subtitle: "Nous transformons votre jour spécial en une expérience inoubliable, de l'arrivée à l'église jusqu'au dernier toast.",
      sposi: {
        title: "Transport des Mariés",
        desc: "La Maserati Quattroporte pour une arrivée de rêve. Élégance italienne pour votre jour le plus important.",
        features: ["Décoration thématique", "Champagne à bord", "Chauffeur en livrée"],
      },
      invitati: {
        title: "Transport des Invités",
        desc: "Minivan et Minibus VIP pour tous vos invités. Personne n'est laissé derrière, tout le monde arrive ensemble.",
        features: ["Jusqu'à 20 passagers par véhicule", "Coordination des horaires", "Service navette"],
      },
      kit: {
        title: "Kit de Bienvenue Personnalisé",
        desc: "Plaids avec vos initiales, guirlandes lumineuses, champagne frais. Chaque détail soigné pour émerveiller.",
        features: ["Plaids personnalisés", "Guirlandes romantiques", "Snacks & boissons premium"],
      },
      gallery: "Nos Aménagements",
      cta: "Demander un Devis Mariage",
      ctaDesc: "Parlez-nous de votre jour spécial et nous créerons le service parfait pour vous.",
    },
  },
  ES: {
    nav: { book: "Reservar" },
    hero: {
      title: "Tu Viaje de Lujo",
      subtitle: "Comienza Aquí",
      desc1: "Conductor profesional · Vehículos de lujo",
      desc2: "Elegancia, puntualidad y confort en cada traslado.",
      cta1: "Solicitar Presupuesto",
      cta2: "Descubrir Flota",
      available: "Disponible 24/7",
      fleet: "Flota de 6 vehículos",
      rates: "Tarifas transparentes",
    },
    about: {
      badge: "SOBRE NOSOTROS",
      title1: "Tu Seguridad,",
      title2: "Nuestra Prioridad",
      desc1: "Durante más de 10 años, Alex Tours Transfer ha sido sinónimo de excelencia en el transporte privado. Cada viaje está cuidadosamente preparado: desde la puntualidad hasta la cortesía, desde la limpieza impecable de los vehículos hasta la profesionalidad de nuestros conductores.",
      desc2: "Ya sea un traslado al aeropuerto, un viaje de negocios o una ocasión especial, nuestro objetivo es uno solo:",
      highlight: "hacerte sentir como en casa, vayas donde vayas.",
      features: ["Conductores Certificados", "Siempre Puntuales", "Vehículos Impecables", "Servicio Personalizado"],
      years: "Años de Experiencia",
      stat1Label: "Clientes Satisfechos",
      stat2Label: "Traslados",
      stat3Label: "Calificación Google",
      punctuality: "Puntualidad",
    },
    services: {
      badge: "NUESTROS SERVICIOS",
      title: "Soluciones a Medida",
      subtitle: "Servicios exclusivos diseñados para satisfacer todas tus necesidades de viaje",
      airport: {
        badge: "PREMIUM",
        title: "Traslados Aeropuerto",
        desc: "Servicio fiable hacia y desde todos los aeropuertos, puertos y estaciones de tren.",
        features: ["30 min de espera gratis", "Seguimiento de vuelos en tiempo real", "Meet & Greet con cartel nominativo"],
      },
      rental: {
        badge: "FLEXIBLE",
        title: "Alquiler por Horas",
        desc: "Conductor y vehículo a tu completa disposición durante el tiempo que necesites.",
        features: ["Mínimo 4 horas, 80 km incluidos", "Flexibilidad total en el itinerario", "Conductor profesional"],
      },
    },
    fleet: {
      badge: "NUESTRA FLOTA",
      title: "Vehículos Premium",
      subtitle: "Modernos, cómodos y perfectamente mantenidos",
      explore: "Explora Nuestros Vehículos",
      exploreDesc: "Haz clic en un vehículo para ver todos los detalles",
      details: "Detalles",
      passengers: "PASAJEROS",
      upTo: "HASTA",
    },
    faq: {
      badge: "PREGUNTAS FRECUENTES",
      title: "¿Tienes Alguna",
      titleHighlight: "Pregunta?",
      subtitle: "Aquí están las respuestas a las preguntas más comunes",
      notFound: "¿No encontraste la respuesta que buscabas?",
      contactBtn: "Escríbenos un Email",
    },
    partnership: {
      badge: "ASOCIACIÓN B2B",
      title: "Asóciate Con Nosotros",
      hotel: {
        title: "Hoteles y Alojamientos",
        desc: "Ofrece a tus huéspedes un servicio de traslado exclusivo hacia y desde los aeropuertos. Aumenta la satisfacción del cliente y el valor percibido de tu establecimiento.",
        features: ["Tarifas contractuales reservadas", "Servicio premium con tu marca"],
      },
      agency: {
        title: "Agencias de Viajes y Tour Operadores",
        desc: "Integra nuestro servicio NCC en tus paquetes turísticos. Garantiza a tus clientes traslados puntuales y cómodos en toda Italia.",
        features: ["Presupuestos dedicados para grupos", "Facturación mensual diferida", "Soporte prioritario 7/7"],
      },
      cta: "¿Empezamos a colaborar?",
      ctaDesc: "Contáctanos para discutir una asociación personalizada. Responderemos en 24 horas.",
      email: "Escribir para Asociación",
      call: "Llámanos",
    },
    summer: {
      badge: "VERANO 2026",
      title: "Tus Destinos de Verano",
      subtitle: "Llega a los lugares más bonitos del Norte de Italia con total comodidad. Traslado privado con conductor, sin estrés.",
      destinations: [
        { name: "Los Lagos", desc: "Lago de Como, Maggiore y Orta: elegancia, villas históricas y relax a un traslado de distancia." },
        { name: "Venecia", desc: "La ciudad sobre el agua te espera. Te llevamos hasta Tronchetto o Piazzale Roma con la máxima puntualidad. Posibilidad de parada en Verona durante el trayecto." },
        { name: "Génova & Liguria", desc: "Mar, Cinque Terre y Portofino: vive la Riviera con comodidad, seguridad y estilo." },
        { name: "La Montaña", desc: "Valle de Aosta y Alpes: Courmayeur, Cervinia y el Mont Blanc. Aire fresco y vistas impresionantes, con total comodidad." },
      ],
      cta: "Reserva Tu Traslado de Verano",
      chooseLabel: "Elige el destino",
    },
    comeFunziona: {
      badge: "SIMPLE Y RÁPIDO",
      title: "Cómo Funciona",
      subtitle: "Reservar tu traslado es fácil: obtén tu presupuesto gratuito en tres simples pasos.",
      steps: [
        { title: "Elige el Servicio", desc: "Selecciona destino, vehículo y fecha de tu viaje." },
        { title: "Envía la Solicitud", desc: "Envíanos los detalles con un clic vía email." },
        { title: "Recibe el Presupuesto", desc: "Respondemos lo antes posible con un presupuesto gratuito y personalizado." },
      ],
      cta: "Solicitar Presupuesto Gratuito",
    },
    cta: {
      available: "vehículos disponibles esta semana",
      title: "Tu Viaje Te Espera",
      subtitle: "Elegancia, comodidad y puntualidad en cada traslado. Reserva tu viaje con total tranquilidad.",
      button: "Solicitar Presupuesto Gratis",
      features: ["Cero Costes Ocultos", "Servicio 24/7"],
      stats: { clients: "Clientes Satisfechos", vehicles: "Vehículos Premium", available: "Siempre Disponibles", rating: "Google" },
    },
    testimonial: {
      quote: "¡Transporte de boda perfecto! Maserati para nosotros y minibus para nuestros invitados. Todos quedaron encantados. ¡Muy recomendable!",
      role: "Recién Casados, Lago de Como 2025",
    },
    footer: {
      quickQuote: "¿Prefieres escribirnos?",
      quickQuoteDesc: "Email activo 24/7 - Respondemos lo antes posible",
      contactBtn: "Escríbenos",
      description: "Servicio NCC de lujo para traslados al aeropuerto y alquiler con conductor en Lombardía, Piamonte y Valle de Aosta.",
      contacts: "Contactos",
      bookingLabel: "Para reservas, escribe a:",
      legalOffice: "Sede Legal",
    },
    cookie: {
      title: "Utilizamos cookies",
      desc: "Este sitio utiliza cookies para mejorar tu experiencia de navegación.",
      reject: "Rechazar",
      accept: "Aceptar",
    },
    wedding: {
      badge: "BODAS & CEREMONIAS",
      title: "Tu Día Especial",
      titleHighlight: "Merece lo Mejor",
      subtitle: "Transformamos tu día especial en una experiencia inolvidable, desde la llegada a la iglesia hasta el último brindis.",
      sposi: {
        title: "Transporte de Novios",
        desc: "El Maserati Quattroporte para una llegada de ensueño. Elegancia italiana para tu día más importante.",
        features: ["Decoración temática", "Champán a bordo", "Chófer de librea"],
      },
      invitati: {
        title: "Transporte de Invitados",
        desc: "Minivan y Minibus VIP para todos tus invitados. Nadie se queda atrás, todos llegan juntos.",
        features: ["Hasta 20 pasajeros por vehículo", "Coordinación de horarios", "Servicio lanzadera"],
      },
      kit: {
        title: "Kit de Bienvenida Personalizado",
        desc: "Mantas con tus iniciales, luces suaves, champán fresco. Cada detalle cuidado para sorprender.",
        features: ["Mantas personalizadas", "Luces románticas", "Snacks y bebidas premium"],
      },
      gallery: "Nuestras Decoraciones",
      cta: "Solicitar Presupuesto Boda",
      ctaDesc: "Cuéntanos sobre tu día especial y crearemos el servicio perfecto para ti.",
    },
    booking: {
      title: "Solicitar Presupuesto",
      subtitle: "Completa el formulario y te responderemos lo antes posible",
      serviceType: "Tipo de Servicio",
      transfer: "Traslado Aeropuerto",
      rental: "Alquiler por Horas",
      from: "Desde",
      fromPlaceholder: "Ej: Aeropuerto Malpensa",
      to: "Hasta",
      toPlaceholder: "Ej: Milán Centro",
      date: "Fecha",
      time: "Hora",
      passengers: "Pasajeros",
      name: "Nombre y Apellido",
      namePlaceholder: "Tu nombre completo",
      phone: "Teléfono",
      phonePlaceholder: "+39 333 123 4567",
      notes: "Notas (opcional)",
      notesPlaceholder: "Número de vuelo, equipaje, sillas para niños...",
      submit: "Enviar Solicitud",
      submitting: "Enviando...",
      close: "Cerrar",
    },
  },
};

// Booking form translations for all languages
const bookingTranslations = {
  IT: {
    title: "Richiedi Preventivo",
    subtitle: "Compila il form e ti risponderemo al più presto",
    serviceType: "Tipo di Servizio",
    transfer: "Trasferimento",
    transferDesc: "Da punto A a B",
    rental: "A Disposizione",
    rentalDesc: "Min. 4 ore, 80 km",
    selectVehicle: "Scegli il Veicolo",
    minivan: "Minivan",
    minivanDesc: "Fino a 8 passeggeri",
    berlina: "Berlina di Lusso",
    berlinaDesc: "Maserati Quattroporte - 3 passeggeri",
    minibus: "Minibus VIP",
    minibusDesc: "Fino a 20 passeggeri",
    from: "Da (Partenza)",
    fromPlaceholder: "Seleziona partenza...",
    to: "A (Destinazione)",
    toPlaceholder: "Seleziona destinazione...",
    searchPlaceholder: "Cerca città, via o indirizzo...",
    customAddress: "Altro indirizzo (via e numero civico)...",
    customItinerary: "📝 Itinerario personalizzato",
    customItineraryDesc: "Inserisci liberamente partenza e destinazione",
    customFromPlaceholder: "es: Via Roma 15, Milano",
    customToPlaceholder: "es: Aeroporto di Roma Fiumicino",
    enterFullAddress: "Inserisci l'indirizzo completo di",
    departure: "partenza",
    destination: "destinazione",
    street: "Via/Piazza",
    streetPlaceholder: "es: Via Roma",
    number: "N°",
    numberPlaceholder: "es: 15",
    city: "Città",
    cityPlaceholder: "es: Milano, Varese, Como...",
    calculatedDistance: "Distanza calcolata",
    estimatedKm: "Km Stimati (obbligatorio per indirizzi personalizzati)",
    enterEstimatedKm: "Inserisci km stimati del percorso",
    useGoogleMaps: "Usa Google Maps per stimare la distanza del percorso",
    date: "Data",
    time: "Ora",
    passengers: "Passeggeri",
    passengerSingular: "passeggero",
    passengerPlural: "passeggeri",
    totalHours: "Ore Totali",
    hours: "ore",
    notes: "Note (opzionale)",
    notesPlaceholder: "Numero volo, bagagli, seggiolini...",
    enterData: "Inserisci i tuoi dati per visualizzare il preventivo",
    name: "Nome",
    namePlaceholder: "Mario",
    surname: "Cognome",
    surnamePlaceholder: "Rossi",
    phone: "Telefono",
    phonePlaceholder: "+39 333 123 4567",
    email: "Email",
    emailPlaceholder: "mario.rossi@email.com",
    summary: "Riepilogo Richiesta",
    service: "Servizio",
    vehicle: "Veicolo",
    route: "Tratta",
    dateTime: "Data e Ora",
    at: "alle",
    estimatedKmLabel: "Km stimati",
    client: "Cliente",
    estimatedQuote: "Preventivo Stimato",
    quoteFreeTitle: "Richiesta Preventivo Gratuito",
    quoteDisclaimer: "Invia la richiesta: ti risponderemo al più presto con un preventivo gratuito e personalizzato.",
    chooseSubmitMethod: "Invia la tua richiesta:",
    sendEmail: "Invia via Email",
    back: "Indietro",
    continue: "Continua",
    seeQuote: "Vedi Preventivo",
    step1: "Servizio",
    step2: "Viaggio",
    step3: "Dati",
    step4: "Preventivo",
    airports: "Aeroporti",
    stations: "Stazioni",
    cities: "Città",
    ports: "Porti",
    airportSelected: "Aeroporto selezionato",
    specifyAddress: "Specifica indirizzo",
    minHoursInfo: "Minimo 4 ore con 80 km inclusi",
    addressHint: "Specifica indirizzo esatto (opzionale per calcolo preciso):",
    calculatingDistance: "Calcolo distanza in corso...",
    estimatedTime: "Tempo stimato",
    minutesLabel: "minuti",
    addressNotFoundTitle: "Attenzione: indirizzo non trovato nella località selezionata",
    originLabel: "Partenza",
    destinationLabel: "Destinazione",
    requestedLabel: "richiesta",
    foundByGoogle: "Indirizzo trovato da Google:",
    addressNotFoundHint: "La via potrebbe non essere indicizzata su Google Maps. Puoi comunque procedere con la richiesta specificando l'indirizzo esatto nel messaggio.",
  },
  EN: {
    title: "Request Quote",
    subtitle: "Fill out the form and we'll respond as soon as possible",
    serviceType: "Service Type",
    transfer: "Transfer",
    transferDesc: "From point A to B",
    rental: "At Disposal",
    rentalDesc: "Min. 4 hours, 80 km",
    selectVehicle: "Choose Vehicle",
    minivan: "Minivan",
    minivanDesc: "Up to 8 passengers",
    berlina: "Luxury Sedan",
    berlinaDesc: "Maserati Quattroporte - 3 passengers",
    minibus: "VIP Minibus",
    minibusDesc: "Up to 20 passengers",
    from: "From (Departure)",
    fromPlaceholder: "Select departure...",
    to: "To (Destination)",
    toPlaceholder: "Select destination...",
    searchPlaceholder: "Search city, street or address...",
    customAddress: "Other address (street and number)...",
    customItinerary: "📝 Custom itinerary",
    customItineraryDesc: "Enter your own departure and destination",
    customFromPlaceholder: "e.g.: Via Roma 15, Milan",
    customToPlaceholder: "e.g.: Rome Fiumicino Airport",
    enterFullAddress: "Enter full address of",
    departure: "departure",
    destination: "destination",
    street: "Street/Square",
    streetPlaceholder: "e.g.: Via Roma",
    number: "No.",
    numberPlaceholder: "e.g.: 15",
    city: "City",
    cityPlaceholder: "e.g.: Milan, Varese, Como...",
    calculatedDistance: "Calculated distance",
    estimatedKm: "Estimated Km (required for custom addresses)",
    enterEstimatedKm: "Enter estimated route km",
    useGoogleMaps: "Use Google Maps to estimate route distance",
    date: "Date",
    time: "Time",
    passengers: "Passengers",
    passengerSingular: "passenger",
    passengerPlural: "passengers",
    totalHours: "Total Hours",
    hours: "hours",
    notes: "Notes (optional)",
    notesPlaceholder: "Flight number, luggage, child seats...",
    enterData: "Enter your details to view the quote",
    name: "First Name",
    namePlaceholder: "John",
    surname: "Last Name",
    surnamePlaceholder: "Smith",
    phone: "Phone",
    phonePlaceholder: "+39 333 123 4567",
    email: "Email",
    emailPlaceholder: "john.smith@email.com",
    summary: "Request Summary",
    service: "Service",
    vehicle: "Vehicle",
    route: "Route",
    dateTime: "Date and Time",
    at: "at",
    estimatedKmLabel: "Estimated km",
    client: "Client",
    estimatedQuote: "Estimated Quote",
    quoteFreeTitle: "Free Quote Request",
    quoteDisclaimer: "Send your request: we'll reply as soon as possible with a free, personalized quote.",
    chooseSubmitMethod: "Send your request:",
    sendEmail: "Send via Email",
    back: "Back",
    continue: "Continue",
    seeQuote: "See Quote",
    step1: "Service",
    step2: "Trip",
    step3: "Details",
    step4: "Quote",
    airports: "Airports",
    stations: "Stations",
    cities: "Cities",
    ports: "Ports",
    airportSelected: "Airport selected",
    specifyAddress: "Specify address",
    minHoursInfo: "Minimum 4 hours with 80 km included",
  },
  DE: {
    title: "Angebot Anfordern",
    subtitle: "Füllen Sie das Formular aus und wir antworten so schnell wie möglich",
    serviceType: "Serviceart",
    transfer: "Transfer",
    transferDesc: "Von Punkt A nach B",
    rental: "Zur Verfügung",
    rentalDesc: "Min. 4 Stunden, 80 km",
    selectVehicle: "Fahrzeug Wählen",
    minivan: "Minivan",
    minivanDesc: "Bis zu 8 Passagiere",
    berlina: "Luxuslimousine",
    berlinaDesc: "Maserati Quattroporte - 3 Passagiere",
    minibus: "VIP Minibus",
    minibusDesc: "Bis zu 20 Passagiere",
    from: "Von (Abfahrt)",
    fromPlaceholder: "Abfahrt wählen...",
    to: "Nach (Ziel)",
    toPlaceholder: "Ziel wählen...",
    customAddress: "Andere Adresse (Straße und Nummer)...",
    customItinerary: "📝 Individueller Reiseplan",
    customItineraryDesc: "Geben Sie Abfahrt und Ziel frei ein",
    customFromPlaceholder: "z.B.: Via Roma 15, Mailand",
    customToPlaceholder: "z.B.: Flughafen Rom Fiumicino",
    enterFullAddress: "Geben Sie die vollständige Adresse ein für",
    departure: "Abfahrt",
    destination: "Ziel",
    street: "Straße/Platz",
    streetPlaceholder: "z.B.: Via Roma",
    number: "Nr.",
    numberPlaceholder: "z.B.: 15",
    city: "Stadt",
    cityPlaceholder: "z.B.: Mailand, Varese, Como...",
    calculatedDistance: "Berechnete Entfernung",
    estimatedKm: "Geschätzte Km (erforderlich für benutzerdefinierte Adressen)",
    enterEstimatedKm: "Geschätzte Strecken-km eingeben",
    useGoogleMaps: "Google Maps zur Entfernungsschätzung verwenden",
    date: "Datum",
    time: "Uhrzeit",
    passengers: "Passagiere",
    passengerSingular: "Passagier",
    passengerPlural: "Passagiere",
    totalHours: "Gesamtstunden",
    hours: "Stunden",
    notes: "Anmerkungen (optional)",
    notesPlaceholder: "Flugnummer, Gepäck, Kindersitze...",
    enterData: "Geben Sie Ihre Daten ein, um das Angebot zu sehen",
    name: "Vorname",
    namePlaceholder: "Max",
    surname: "Nachname",
    surnamePlaceholder: "Mustermann",
    phone: "Telefon",
    phonePlaceholder: "+39 333 123 4567",
    email: "E-Mail",
    emailPlaceholder: "max.mustermann@email.com",
    summary: "Anfrage Zusammenfassung",
    service: "Service",
    vehicle: "Fahrzeug",
    route: "Strecke",
    dateTime: "Datum und Uhrzeit",
    at: "um",
    estimatedKmLabel: "Geschätzte km",
    client: "Kunde",
    estimatedQuote: "Geschätztes Angebot",
    quoteFreeTitle: "Kostenlose Angebotsanfrage",
    quoteDisclaimer: "Senden Sie Ihre Anfrage: Wir antworten so schnell wie möglich mit einem kostenlosen, persönlichen Angebot.",
    chooseSubmitMethod: "Senden Sie Ihre Anfrage:",
    sendEmail: "Per E-Mail senden",
    back: "Zurück",
    continue: "Weiter",
    seeQuote: "Angebot Ansehen",
    step1: "Service",
    step2: "Reise",
    step3: "Daten",
    step4: "Angebot",
    airports: "Flughäfen",
    stations: "Bahnhöfe",
    cities: "Städte",
    ports: "Häfen",
    airportSelected: "Flughafen ausgewählt",
    specifyAddress: "Adresse angeben",
    minHoursInfo: "Mindestens 4 Stunden mit 80 km inklusive",
    searchPlaceholder: "Stadt, Straße oder Adresse suchen...",
    addressHint: "Genaue Adresse angeben (optional für präzise Berechnung):",
    calculatingDistance: "Entfernung wird berechnet...",
    estimatedTime: "Geschätzte Zeit",
    minutesLabel: "Minuten",
    addressNotFoundTitle: "Achtung: Adresse am gewählten Ort nicht gefunden",
    originLabel: "Abfahrt",
    destinationLabel: "Ziel",
    requestedLabel: "angefragt",
    foundByGoogle: "Von Google gefundene Adresse:",
    addressNotFoundHint: "Die Straße ist möglicherweise nicht auf Google Maps indexiert. Sie können die Anfrage trotzdem senden und die genaue Adresse in der Nachricht angeben.",
  },
  FR: {
    title: "Demander un Devis",
    subtitle: "Remplissez le formulaire et nous répondrons dès que possible",
    serviceType: "Type de Service",
    transfer: "Transfert",
    transferDesc: "Du point A au point B",
    rental: "À Disposition",
    rentalDesc: "Min. 4 heures, 80 km",
    selectVehicle: "Choisir le Véhicule",
    minivan: "Minivan",
    minivanDesc: "Jusqu'à 8 passagers",
    berlina: "Berline de Luxe",
    berlinaDesc: "Maserati Quattroporte - 3 passagers",
    minibus: "Minibus VIP",
    minibusDesc: "Jusqu'à 20 passagers",
    from: "De (Départ)",
    fromPlaceholder: "Sélectionner départ...",
    to: "À (Destination)",
    toPlaceholder: "Sélectionner destination...",
    customAddress: "Autre adresse (rue et numéro)...",
    customItinerary: "📝 Itinéraire personnalisé",
    customItineraryDesc: "Entrez librement départ et destination",
    customFromPlaceholder: "ex: Via Roma 15, Milan",
    customToPlaceholder: "ex: Aéroport Rome Fiumicino",
    enterFullAddress: "Entrez l'adresse complète de",
    departure: "départ",
    destination: "destination",
    street: "Rue/Place",
    streetPlaceholder: "ex: Via Roma",
    number: "N°",
    numberPlaceholder: "ex: 15",
    city: "Ville",
    cityPlaceholder: "ex: Milan, Varese, Como...",
    calculatedDistance: "Distance calculée",
    estimatedKm: "Km Estimés (requis pour adresses personnalisées)",
    enterEstimatedKm: "Entrez les km estimés du trajet",
    useGoogleMaps: "Utilisez Google Maps pour estimer la distance",
    date: "Date",
    time: "Heure",
    passengers: "Passagers",
    passengerSingular: "passager",
    passengerPlural: "passagers",
    totalHours: "Heures Totales",
    hours: "heures",
    notes: "Notes (optionnel)",
    notesPlaceholder: "Numéro de vol, bagages, sièges enfants...",
    enterData: "Entrez vos coordonnées pour voir le devis",
    name: "Prénom",
    namePlaceholder: "Jean",
    surname: "Nom",
    surnamePlaceholder: "Dupont",
    phone: "Téléphone",
    phonePlaceholder: "+39 333 123 4567",
    email: "Email",
    emailPlaceholder: "jean.dupont@email.com",
    summary: "Résumé de la Demande",
    service: "Service",
    vehicle: "Véhicule",
    route: "Trajet",
    dateTime: "Date et Heure",
    at: "à",
    estimatedKmLabel: "Km estimés",
    client: "Client",
    estimatedQuote: "Devis Estimé",
    quoteFreeTitle: "Demande de Devis Gratuit",
    quoteDisclaimer: "Envoyez votre demande : nous vous répondrons dès que possible avec un devis gratuit et personnalisé.",
    chooseSubmitMethod: "Comment souhaitez-vous nous envoyer la demande?",
    sendEmail: "Envoyer par Email",
    back: "Retour",
    continue: "Continuer",
    seeQuote: "Voir le Devis",
    step1: "Service",
    step2: "Voyage",
    step3: "Données",
    step4: "Devis",
    airports: "Aéroports",
    stations: "Gares",
    cities: "Villes",
    ports: "Ports",
    airportSelected: "Aéroport sélectionné",
    specifyAddress: "Préciser l'adresse",
    minHoursInfo: "Minimum 4 heures avec 80 km inclus",
    searchPlaceholder: "Rechercher ville, rue ou adresse...",
    addressHint: "Spécifiez l'adresse exacte (optionnel pour un calcul précis) :",
    calculatingDistance: "Calcul de la distance en cours...",
    estimatedTime: "Temps estimé",
    minutesLabel: "minutes",
    addressNotFoundTitle: "Attention : adresse introuvable dans le lieu sélectionné",
    originLabel: "Départ",
    destinationLabel: "Destination",
    requestedLabel: "demandé",
    foundByGoogle: "Adresse trouvée par Google :",
    addressNotFoundHint: "La rue n'est peut-être pas indexée sur Google Maps. Vous pouvez quand même poursuivre la demande en précisant l'adresse exacte dans le message.",
  },
  ES: {
    title: "Solicitar Presupuesto",
    subtitle: "Completa el formulario y te responderemos lo antes posible",
    serviceType: "Tipo de Servicio",
    transfer: "Traslado",
    transferDesc: "Del punto A al B",
    rental: "A Disposición",
    rentalDesc: "Mín. 4 horas, 80 km",
    selectVehicle: "Elegir Vehículo",
    minivan: "Minivan",
    minivanDesc: "Hasta 8 pasajeros",
    berlina: "Berlina de Lujo",
    berlinaDesc: "Maserati Quattroporte - 3 pasajeros",
    minibus: "Minibus VIP",
    minibusDesc: "Hasta 20 pasajeros",
    from: "Desde (Salida)",
    fromPlaceholder: "Seleccionar salida...",
    to: "Hasta (Destino)",
    toPlaceholder: "Seleccionar destino...",
    customAddress: "Otra dirección (calle y número)...",
    customItinerary: "📝 Itinerario personalizado",
    customItineraryDesc: "Ingresa libremente salida y destino",
    customFromPlaceholder: "ej: Via Roma 15, Milán",
    customToPlaceholder: "ej: Aeropuerto Roma Fiumicino",
    enterFullAddress: "Ingresa la dirección completa de",
    departure: "salida",
    destination: "destino",
    street: "Calle/Plaza",
    streetPlaceholder: "ej: Via Roma",
    number: "N°",
    numberPlaceholder: "ej: 15",
    city: "Ciudad",
    cityPlaceholder: "ej: Milán, Varese, Como...",
    calculatedDistance: "Distancia calculada",
    estimatedKm: "Km Estimados (requerido para direcciones personalizadas)",
    enterEstimatedKm: "Ingresa km estimados del recorrido",
    useGoogleMaps: "Usa Google Maps para estimar la distancia",
    date: "Fecha",
    time: "Hora",
    passengers: "Pasajeros",
    passengerSingular: "pasajero",
    passengerPlural: "pasajeros",
    totalHours: "Horas Totales",
    hours: "horas",
    notes: "Notas (opcional)",
    notesPlaceholder: "Número de vuelo, equipaje, sillas para niños...",
    enterData: "Ingresa tus datos para ver el presupuesto",
    name: "Nombre",
    namePlaceholder: "Juan",
    surname: "Apellido",
    surnamePlaceholder: "García",
    phone: "Teléfono",
    phonePlaceholder: "+39 333 123 4567",
    email: "Email",
    emailPlaceholder: "juan.garcia@email.com",
    summary: "Resumen de la Solicitud",
    service: "Servicio",
    vehicle: "Vehículo",
    route: "Ruta",
    dateTime: "Fecha y Hora",
    at: "a las",
    estimatedKmLabel: "Km estimados",
    client: "Cliente",
    estimatedQuote: "Presupuesto Estimado",
    quoteFreeTitle: "Solicitud de Presupuesto Gratuito",
    quoteDisclaimer: "Envía tu solicitud: te responderemos lo antes posible con un presupuesto gratuito y personalizado.",
    chooseSubmitMethod: "Envía tu solicitud:",
    sendEmail: "Enviar por Email",
    back: "Atrás",
    continue: "Continuar",
    seeQuote: "Ver Presupuesto",
    step1: "Servicio",
    step2: "Viaje",
    step3: "Datos",
    step4: "Presupuesto",
    airports: "Aeropuertos",
    stations: "Estaciones",
    cities: "Ciudades",
    ports: "Puertos",
    airportSelected: "Aeropuerto seleccionado",
    specifyAddress: "Especificar dirección",
    minHoursInfo: "Mínimo 4 horas con 80 km incluidos",
    searchPlaceholder: "Buscar ciudad, calle o dirección...",
    addressHint: "Especifica la dirección exacta (opcional para cálculo preciso):",
    calculatingDistance: "Calculando distancia...",
    estimatedTime: "Tiempo estimado",
    minutesLabel: "minutos",
    addressNotFoundTitle: "Atención: dirección no encontrada en la ubicación seleccionada",
    originLabel: "Salida",
    destinationLabel: "Destino",
    requestedLabel: "solicitada",
    foundByGoogle: "Dirección encontrada por Google:",
    addressNotFoundHint: "La calle puede no estar indexada en Google Maps. Puedes continuar con la solicitud especificando la dirección exacta en el mensaje.",
  },
};

// Get translation helper
const getT = (lang) => translations[lang] || translations.IT;

// Vehicles data
const getVehicles = (lang) => {
  const isIT = lang === 'IT';
  const isDE = lang === 'DE';
  const isFR = lang === 'FR';
  const isES = lang === 'ES';
  
  return [
    {
      id: 1,
      name: "Minivan",
      capacity: "8",
      image: IMAGES.vwCaravelle,
      models: ["Volkswagen Caravelle", "Mercedes Vito", "Opel Zafira Life", "Ford Tourneo Custom"],
      premium: false,
    },
    {
      id: 2,
      name: isIT ? "Berlina di Lusso" : isDE ? "Luxuslimousine" : isFR ? "Berline de Luxe" : isES ? "Berlina de Lujo" : "Luxury Sedan",
      capacity: "3",
      image: IMAGES.luxurySedan,
      models: [
        "Maserati Quattroporte", 
        isIT ? "Sedili in pelle premium" : isDE ? "Premium-Ledersitze" : isFR ? "Sièges en cuir premium" : isES ? "Asientos de cuero premium" : "Premium leather seats",
        isIT ? "Business & Cerimonie" : isDE ? "Business & Zeremonien" : isFR ? "Business & Cérémonies" : isES ? "Negocios & Ceremonias" : "Business & Ceremonies"
      ],
      premium: true,
    },
    {
      id: 3,
      name: "Minibus VIP",
      capacity: "20",
      image: IMAGES.minibus,
      models: [
        "Mercedes Sprinter", 
        isIT ? "Interni VIP" : isDE ? "VIP-Innenraum" : isFR ? "Intérieur VIP" : isES ? "Interior VIP" : "VIP Interior",
        isIT ? "Ideale per gruppi" : isDE ? "Ideal für Gruppen" : isFR ? "Idéal pour groupes" : isES ? "Ideal para grupos" : "Ideal for groups"
      ],
      premium: false,
    },
  ];
};

// Gallery vehicles - come nel sito originale con descrizioni complete
const vehicleDetails = {
  "Ford Tourneo Custom": {
    name: "Ford Tourneo Custom",
    image: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/nznlkefn_Ford%20Tourneo%20Custo%202025.png",
    images: [
      "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/nznlkefn_Ford%20Tourneo%20Custo%202025.png",
      "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/9yk1q0xg_Ford%20interni.jpeg"
    ],
    versions: [
      {
        title: "Versione 8+1 posti",
        subtitle: "Per gruppi numerosi senza rinunce",
        desc: "Perfetto per famiglie numerose, piccoli gruppi turistici o team aziendali, questo modello offre 8 sedili passeggeri più il conducente, disposti in configurazione face-to-face. L'abitacolo è arioso, ben organizzato e rifinito con cura. I passeggeri viaggiano comodi, con più spazio per le gambe e una capacità di carico superiore rispetto alla media della categoria."
      },
      {
        title: "Versione 7+1 posti",
        subtitle: "Più spazio individuale, stesso comfort",
        desc: "Questa variante, con 7 sedili passeggeri e configurazione 'face-to-face', è pensata per offrire ancora più spazio personale. Ogni sedile è ampio e comodo, con accesso facilitato. Ottima scelta per servizi VIP, clienti business o viaggi che richiedono comfort extra e discrezione."
      }
    ],
    features: [
      { icon: "❄️", title: "Climatizzazione Multi-Zona" },
      { icon: "🔇", title: "Silenziosità di Marcia" },
      { icon: "🧳", title: "Ampio Bagagliaio" },
      { icon: "✨", title: "Linee Eleganti" }
    ]
  },
  "Opel Zafira Life": {
    name: "Opel Zafira Life",
    image: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/v7yr8u87_Opel%20zafira%20Life.webp",
    images: [
      "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/v7yr8u87_Opel%20zafira%20Life.webp"
    ],
    tagline: "Versatilità e comfort per ogni occasione",
    description: "L'Opel Zafira Life rappresenta la soluzione perfetta per chi cerca un equilibrio ideale tra spaziosità, comfort e praticità, senza rinunciare all'eleganza e alla tecnologia.",
    versions: [
      {
        title: "Versione 7+1 posti",
        subtitle: "Comfort Premium per Famiglie",
        desc: "Ideale per famiglie numerose e piccoli gruppi, questa configurazione offre 7 posti passeggeri in un ambiente spazioso e confortevole. Gli interni sono curati nei minimi dettagli, con materiali di qualità e finiture eleganti. Perfetto per viaggi famiglia, gite turistiche e trasferimenti di gruppo."
      },
      {
        title: "Versione 8+1 posti",
        subtitle: "Massima Capacità, Stesso Comfort",
        desc: "La versione con 8 posti passeggeri è pensata per chi ha bisogno di maggiore capacità senza compromessi sul comfort. Perfetta per gruppi aziendali, eventi e trasferimenti aeroportuali. Ideale per team aziendali, gruppi turistici e eventi speciali."
      }
    ],
    features: [
      { icon: "🎨", title: "Design Moderno" },
      { icon: "🛡️", title: "Sicurezza Avanzata" },
      { icon: "⚡", title: "Tecnologia Integrata" },
      { icon: "❄️", title: "Clima Bi-Zona" }
    ]
  },
  "VW Caravelle": {
    name: "Volkswagen Caravelle",
    image: IMAGES.vwCaravelle,
    images: [
      IMAGES.vwCaravelle
    ],
    tagline: "Il comfort di gruppo, pensato per te",
    description: "Se viaggi in gruppo, con colleghi, amici o famiglia, il nostro Volkswagen Caravelle è la scelta perfetta per spostarti con comodità, spazio e tranquillità. Questo veicolo può accogliere fino a 8 passeggeri, garantendo sedili comodi, ampio spazio per i bagagli e un abitacolo silenzioso e climatizzato. È ideale per transfer aeroportuali, escursioni, viaggi aziendali o eventi.",
    highlights: [
      { icon: "👥", title: "Fino a 8 Passeggeri", desc: "Spazio abbondante per gruppi e famiglie" },
      { icon: "🛡️", title: "Sicurezza Avanzata", desc: "Sistemi di sicurezza sempre operativi" },
      { icon: "🇩🇪", title: "Efficienza Tedesca", desc: "Qualità Volkswagen garantita" }
    ],
    useCases: ["✈️ Transfer Aeroportuali", "🏢 Viaggi Aziendali", "🎉 Eventi Speciali", "🗺️ Escursioni"]
  },
  "Mercedes Vito": {
    name: "Mercedes-Benz Vito",
    image: IMAGES.mercedesVito,
    images: [
      IMAGES.mercedesVito
    ],
    tagline: "Viaggiare con stile, in un ambiente esclusivo",
    description: "Il Mercedes Vito è la scelta perfetta per chi desidera un servizio di trasporto elegante, riservato e confortevole. Ideale per clienti business, piccoli gruppi o ospiti VIP. Gli interni in pelle, la disposizione face to face dei sedili e la cura per i dettagli creano un ambiente accogliente e professionale. Il Vito può accogliere fino a 8 passeggeri, con buono spazio per i bagagli.",
    highlights: [
      { icon: "💺", title: "Interni in Pelle", desc: "Sedili premium con finiture Mercedes-Benz di altissima qualità" },
      { icon: "🔄", title: "Face to Face", desc: "Disposizione ravvicinata per atmosfera riservata" },
      { icon: "💼", title: "Business Class", desc: "Ideale per clienti VIP e meeting in movimento" }
    ],
    features: [
      { icon: "🎩", title: "Atmosfera Riservata" },
      { icon: "🤝", title: "Meeting in Movimento" },
      { icon: "🔇", title: "Abitacolo Silenzioso" },
      { icon: "❄️", title: "Clima Indipendente" }
    ]
  },
  "Maserati Quattroporte": {
    name: "Maserati Quattroporte",
    image: IMAGES.maseratiGallery,
    images: [
      IMAGES.maseratiGallery,
      IMAGES.maserati
    ],
    tagline: "Eleganza e Prestigio per i Tuoi Spostamenti",
    description: "La Maserati Quattroporte è la sintesi perfetta tra lusso, comfort e prestazioni. Ideale per trasferimenti aziendali, eventi speciali e viaggi esclusivi.",
    highlights: [
      { icon: "🇮🇹", title: "Design Italiano Inconfondibile", desc: "Elegante e sportivo, rappresenta il meglio del made in Italy" },
      { icon: "💎", title: "Interni in Pelle Pregiata", desc: "Finiture di altissima qualità per il massimo lusso" },
      { icon: "🛋️", title: "Sedili Posteriori Spaziosi", desc: "Perfetti per rilassarsi o lavorare durante il tragitto" },
      { icon: "❄️", title: "Clima Bi-Zona", desc: "Controllo temperatura personalizzato" }
    ],
    useCases: ["🏢 Trasferimenti Aziendali", "🎭 Eventi Speciali", "✨ Viaggi Esclusivi"],
    premium: true
  },
  "Mercedes Sprinter": {
    name: "Mercedes Sprinter VIP",
    image: IMAGES.sprinter,
    images: [
      IMAGES.sprinter,
      IMAGES.interiorSprinter1,
      IMAGES.interiorSprinter2,
      IMAGES.interiorSprinter3,
      IMAGES.interiorSprinter4
    ],
    tagline: "Allestimento VIP per Trasporti di Classe",
    description: "Un'eccezionale combinazione di stile e funzionalità che eleva il concetto di trasporto. Ideale per eventi aziendali, trasferimenti di gruppo o occasioni speciali.",
    highlights: [
      { icon: "👥", title: "Massima Capacità", desc: "20 posti + conducente. Trasporta gruppi numerosi senza compromettere il comfort" },
      { icon: "💎", title: "Interni Raffinati", desc: "Finiture eleganti e materiali pregiati. Ogni sedile è dotato di un tavolino davanti" },
      { icon: "📱", title: "Tecnologia Innovativa", desc: "Sistema di intrattenimento e climatizzazione personalizzabile" },
      { icon: "🧳", title: "Spazio Bagagli", desc: "Ogni passeggero può portare un bagaglio (trolley)" }
    ],
    useCases: ["🏢 Eventi Aziendali", "✈️ Trasferimenti Gruppo", "🎉 Occasioni Speciali"]
  }
};

const galleryVehicles = [
  { name: "Ford Tourneo Custom", image: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/nznlkefn_Ford%20Tourneo%20Custo%202025.png" },
  { name: "Opel Zafira Life", image: "https://customer-assets.emergentagent.com/job_chat-recovery-20/artifacts/v7yr8u87_Opel%20zafira%20Life.webp" },
  { name: "VW Caravelle", image: IMAGES.vwCaravelle },
  { name: "Mercedes Vito", image: IMAGES.mercedesVito },
  { name: "Maserati Quattroporte", image: IMAGES.maseratiGallery },
  { name: "Mercedes Sprinter", image: IMAGES.sprinter },
];

// FAQ data with all translations
const getFaqData = (lang) => {
  const faqByLang = {
    IT: [
      { q: "Come posso prenotare un trasferimento?", a: "Puoi richiedere un preventivo gratuito direttamente dal nostro sito cliccando su \"Richiedi Preventivo\". In alternativa, scrivi a prenotazioni.alexncc@gmail.com. Ti risponderemo al più presto con tutti i dettagli e un preventivo personalizzato." },
      { q: "Quali metodi di pagamento accettate?", a: "Accettiamo contanti, carte di credito/debito (Visa, Mastercard, American Express) e bonifico bancario. Per i clienti business offriamo anche fatturazione posticipata." },
      { q: "Cosa succede se il mio volo è in ritardo?", a: "Nessun problema! Monitoriamo tutti i voli in tempo reale. Il nostro autista sarà presente all'arrivo effettivo del tuo volo, senza costi aggiuntivi per ritardi dovuti alla compagnia aerea. Ti aspetteremo con un cartello con il tuo nome all'uscita arrivi." },
      { q: "Posso modificare o cancellare la prenotazione?", a: "Sì, offriamo cancellazione gratuita fino a 24 ore prima del servizio. Per modifiche (orario, indirizzo, numero passeggeri), contattaci e faremo il possibile per accontentarti senza costi aggiuntivi." },
      { q: "Fornite seggiolini per bambini?", a: "Certamente! Disponiamo di seggiolini omologati per tutte le età (navicella, ovetto, seggiolino, rialzo). Basta indicarlo al momento della prenotazione e lo prepareremo senza costi aggiuntivi." },
      { q: "Quanto costa un trasferimento per Malpensa?", a: "Offriamo tariffe fisse per le tratte più comuni (es. Milano Centro ↔ Malpensa). Il prezzo dipende dal tipo di veicolo e dalla destinazione esatta. Richiedi un preventivo gratuito per conoscere il prezzo esatto del tuo trasferimento!" },
    ],
    EN: [
      { q: "How can I book a transfer?", a: "You can request a free quote directly from our website by clicking \"Request Quote\". Alternatively, write to prenotazioni.alexncc@gmail.com. We'll respond as soon as possible with all details and a personalized quote." },
      { q: "What payment methods do you accept?", a: "We accept cash, credit/debit cards (Visa, Mastercard, American Express), and bank transfer. For business clients, we also offer deferred billing." },
      { q: "What happens if my flight is delayed?", a: "No problem! We monitor all flights in real-time. Our driver will be present at your actual arrival, with no additional costs for airline delays. We'll wait for you with a sign with your name at the arrivals exit." },
      { q: "Can I modify or cancel my booking?", a: "Yes, we offer free cancellation up to 24 hours before the service. For changes (time, address, number of passengers), contact us and we'll do our best to accommodate you at no additional cost." },
      { q: "Do you provide child seats?", a: "Certainly! We have certified seats for all ages (carrycot, infant seat, child seat, booster). Just indicate it when booking and we'll prepare it at no additional cost." },
      { q: "How much does a transfer to Malpensa cost?", a: "We offer fixed rates for the most common routes (e.g., Milan Center ↔ Malpensa). The price depends on the vehicle type and exact destination. Request a free quote to know the exact price of your transfer!" },
    ],
    DE: [
      { q: "Wie kann ich einen Transfer buchen?", a: "Sie können ein kostenloses Angebot direkt auf unserer Website anfordern, indem Sie auf \"Angebot Anfordern\" klicken. Alternativ schreiben Sie an prenotazioni.alexncc@gmail.com. Wir antworten so schnell wie möglich mit allen Details und einem persönlichen Angebot." },
      { q: "Welche Zahlungsmethoden akzeptieren Sie?", a: "Wir akzeptieren Bargeld, Kredit-/Debitkarten (Visa, Mastercard, American Express) und Banküberweisung. Für Geschäftskunden bieten wir auch nachträgliche Abrechnung an." },
      { q: "Was passiert, wenn mein Flug verspätet ist?", a: "Kein Problem! Wir überwachen alle Flüge in Echtzeit. Unser Fahrer wird bei Ihrer tatsächlichen Ankunft anwesend sein, ohne zusätzliche Kosten für Flugverspätungen. Wir warten am Ausgang Ankunft mit einem Schild mit Ihrem Namen." },
      { q: "Kann ich meine Buchung ändern oder stornieren?", a: "Ja, wir bieten kostenlose Stornierung bis 24 Stunden vor dem Service. Bei Änderungen (Zeit, Adresse, Personenzahl) kontaktieren Sie uns und wir tun unser Bestes, um Sie ohne zusätzliche Kosten zufrieden zu stellen." },
      { q: "Bieten Sie Kindersitze an?", a: "Selbstverständlich! Wir haben zertifizierte Sitze für alle Altersgruppen (Babywanne, Babyschale, Kindersitz, Sitzerhöhung). Geben Sie es einfach bei der Buchung an und wir bereiten es ohne zusätzliche Kosten vor." },
      { q: "Wie viel kostet ein Transfer nach Malpensa?", a: "Wir bieten Festpreise für die gängigsten Strecken (z.B. Mailand Zentrum ↔ Malpensa). Der Preis hängt vom Fahrzeugtyp und dem genauen Ziel ab. Fordern Sie ein kostenloses Angebot an, um den genauen Preis für Ihren Transfer zu erfahren!" },
    ],
    FR: [
      { q: "Comment puis-je réserver un transfert?", a: "Vous pouvez demander un devis gratuit directement sur notre site en cliquant sur \"Demander un Devis\". Alternativement, écrivez à prenotazioni.alexncc@gmail.com. Nous répondrons dès que possible avec tous les détails et un devis personnalisé." },
      { q: "Quels modes de paiement acceptez-vous?", a: "Nous acceptons les espèces, les cartes de crédit/débit (Visa, Mastercard, American Express) et les virements bancaires. Pour les clients professionnels, nous offrons également la facturation différée." },
      { q: "Que se passe-t-il si mon vol est retardé?", a: "Pas de problème! Nous surveillons tous les vols en temps réel. Notre chauffeur sera présent à votre arrivée effective, sans frais supplémentaires pour les retards aériens. Nous vous attendrons avec une pancarte à votre nom à la sortie des arrivées." },
      { q: "Puis-je modifier ou annuler ma réservation?", a: "Oui, nous offrons l'annulation gratuite jusqu'à 24 heures avant le service. Pour les modifications (heure, adresse, nombre de passagers), contactez-nous et nous ferons de notre mieux pour vous satisfaire sans frais supplémentaires." },
      { q: "Fournissez-vous des sièges pour enfants?", a: "Certainement! Nous avons des sièges homologués pour tous les âges (nacelle, cosy, siège enfant, rehausseur). Il suffit de l'indiquer lors de la réservation et nous le préparerons sans frais supplémentaires." },
      { q: "Combien coûte un transfert vers Malpensa?", a: "Nous offrons des tarifs fixes pour les trajets les plus courants (ex. Centre de Milan ↔ Malpensa). Le prix dépend du type de véhicule et de la destination exacte. Demandez un devis gratuit pour connaître le prix exact de votre transfert!" },
    ],
    ES: [
      { q: "¿Cómo puedo reservar un traslado?", a: "Puedes solicitar un presupuesto gratuito directamente desde nuestro sitio haciendo clic en \"Solicitar Presupuesto\". Alternativamente, escribe a prenotazioni.alexncc@gmail.com. Responderemos lo antes posible con todos los detalles y un presupuesto personalizado." },
      { q: "¿Qué métodos de pago aceptáis?", a: "Aceptamos efectivo, tarjetas de crédito/débito (Visa, Mastercard, American Express) y transferencia bancaria. Para clientes empresariales, también ofrecemos facturación diferida." },
      { q: "¿Qué pasa si mi vuelo se retrasa?", a: "¡Sin problema! Monitorizamos todos los vuelos en tiempo real. Nuestro conductor estará presente en tu llegada real, sin costes adicionales por retrasos de la aerolínea. Te esperaremos con un cartel con tu nombre en la salida de llegadas." },
      { q: "¿Puedo modificar o cancelar mi reserva?", a: "Sí, ofrecemos cancelación gratuita hasta 24 horas antes del servicio. Para cambios (hora, dirección, número de pasajeros), contáctanos y haremos todo lo posible para satisfacerte sin costes adicionales." },
      { q: "¿Proporcionáis sillas para niños?", a: "¡Por supuesto! Tenemos sillas homologadas para todas las edades (capazo, grupo 0, silla, elevador). Solo indícalo al reservar y lo prepararemos sin costes adicionales." },
      { q: "¿Cuánto cuesta un traslado a Malpensa?", a: "Ofrecemos tarifas fijas para las rutas más comunes (ej. Centro de Milán ↔ Malpensa). El precio depende del tipo de vehículo y el destino exacto. ¡Solicita un presupuesto gratuito para conocer el precio exacto de tu traslado!" },
    ],
  };
  return faqByLang[lang] || faqByLang.IT;
};

// Email Button
const EmailButton = () => (
  <a
    href="mailto:prenotazioni.alexncc@gmail.com?subject=Richiesta%20Informazioni%20NCC&body=Ciao!%20Vorrei%20informazioni%20sul%20servizio%20NCC."
    className="fixed left-4 bottom-24 z-40 bg-gradient-to-br from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110"
    data-testid="email-button"
    aria-label="Scrivici una email"
  >
    <Mail className="w-6 h-6" />
  </a>
);

// Booking Modal
// Tariffe per calcolo preventivo
const TARIFFE = {
  transfer: { // €/km
    berlina: 3.50,
    minivan: 3.00,
    minibus: 4.00,
  },
  disposizione: {
    berlina: { base: 320, oraExtra: 80, kmExtra: 3.50 },
    minivan: { base: 280, oraExtra: 70, kmExtra: 3.00 },
    minibus: { base: 400, oraExtra: 100, kmExtra: 4.00 },
  },
  kmInclusi: 80,
  oreMinime: 4,
};

// Località predefinite con coordinate per calcolo distanze
const LOCATIONS = [
  // Aeroporti
  { id: 'malpensa', name: 'Aeroporto Malpensa (MXP)', lat: 45.6306, lng: 8.7231, type: 'aeroporto' },
  { id: 'linate', name: 'Aeroporto Linate (LIN)', lat: 45.4537, lng: 9.2765, type: 'aeroporto' },
  { id: 'orio', name: 'Aeroporto Bergamo Orio (BGY)', lat: 45.6739, lng: 9.7042, type: 'aeroporto' },
  { id: 'torino_caselle', name: 'Aeroporto Torino Caselle (TRN)', lat: 45.2008, lng: 7.6497, type: 'aeroporto' },
  // Porti
  { id: 'genova_porto', name: 'Porto di Genova (Terminal Crociere)', lat: 44.4097, lng: 8.9219, type: 'porto' },
  { id: 'genova_porto_antico', name: 'Porto Antico Genova', lat: 44.4103, lng: 8.9261, type: 'porto' },
  { id: 'genova_porto_traghetti', name: 'Porto di Genova (Terminal Traghetti)', lat: 44.4053, lng: 8.9172, type: 'porto' },
  // Stazioni Ferroviarie
  { id: 'milano_centrale', name: 'Milano Centrale', lat: 45.4861, lng: 9.2047, type: 'stazione' },
  { id: 'milano_porta_garibaldi', name: 'Milano Porta Garibaldi', lat: 45.4847, lng: 9.1878, type: 'stazione' },
  { id: 'milano_rogoredo', name: 'Milano Rogoredo', lat: 45.4336, lng: 9.2381, type: 'stazione' },
  { id: 'torino_porta_nuova', name: 'Torino Porta Nuova', lat: 45.0617, lng: 7.6778, type: 'stazione' },
  { id: 'torino_porta_susa', name: 'Torino Porta Susa', lat: 45.0714, lng: 7.6647, type: 'stazione' },
  { id: 'genova_piazza_principe', name: 'Genova Piazza Principe', lat: 44.4147, lng: 8.9214, type: 'stazione' },
  { id: 'genova_brignole', name: 'Genova Brignole', lat: 44.4069, lng: 8.9467, type: 'stazione' },
  { id: 'varese_fs', name: 'Varese FS', lat: 45.8183, lng: 8.8325, type: 'stazione' },
  { id: 'como_san_giovanni', name: 'Como San Giovanni', lat: 45.8078, lng: 9.0833, type: 'stazione' },
  { id: 'somma_lombardo_fs', name: 'Somma Lombardo FS', lat: 45.6847, lng: 8.7086, type: 'stazione' },
  { id: 'sesto_calende_fs', name: 'Sesto Calende FS', lat: 45.7267, lng: 8.6392, type: 'stazione' },
  { id: 'vergiate_fs', name: 'Vergiate FS', lat: 45.7200, lng: 8.6944, type: 'stazione' },
  { id: 'gallarate_fs', name: 'Gallarate FS', lat: 45.6597, lng: 8.7922, type: 'stazione' },
  { id: 'busto_arsizio_fs', name: 'Busto Arsizio FS', lat: 45.6139, lng: 8.8514, type: 'stazione' },
  { id: 'busto_arsizio_nord', name: 'Busto Arsizio Nord (FN)', lat: 45.6167, lng: 8.8472, type: 'stazione' },
  // Città
  { id: 'milano', name: 'Milano', lat: 45.4642, lng: 9.1900, type: 'città' },
  { id: 'milano_fiera', name: 'Milano Fiera Rho', lat: 45.5204, lng: 9.0917, type: 'fiera' },
  { id: 'varese', name: 'Varese', lat: 45.8206, lng: 8.8257, type: 'città' },
  { id: 'busto_arsizio', name: 'Busto Arsizio', lat: 45.6120, lng: 8.8519, type: 'città' },
  { id: 'gallarate', name: 'Gallarate', lat: 45.6603, lng: 8.7917, type: 'città' },
  { id: 'somma_lombardo', name: 'Somma Lombardo', lat: 45.6833, lng: 8.7000, type: 'città' },
  { id: 'sesto_calende', name: 'Sesto Calende', lat: 45.7267, lng: 8.6392, type: 'città' },
  { id: 'saronno', name: 'Saronno', lat: 45.6261, lng: 9.0364, type: 'città' },
  { id: 'vergiate', name: 'Vergiate', lat: 45.7194, lng: 8.6947, type: 'città' },
  { id: 'como', name: 'Como', lat: 45.8081, lng: 9.0852, type: 'città' },
  { id: 'cernobbio', name: 'Cernobbio', lat: 45.8392, lng: 9.0708, type: 'città' },
  { id: 'bellagio', name: 'Bellagio', lat: 45.9867, lng: 9.2625, type: 'città' },
  { id: 'torino', name: 'Torino', lat: 45.0703, lng: 7.6869, type: 'città' },
  { id: 'genova', name: 'Genova', lat: 44.4056, lng: 8.9463, type: 'città' },
  { id: 'stresa', name: 'Stresa', lat: 45.8833, lng: 8.5333, type: 'città' },
  { id: 'arona', name: 'Arona', lat: 45.7603, lng: 8.5572, type: 'città' },
  { id: 'lugano', name: 'Lugano (CH)', lat: 46.0037, lng: 8.9511, type: 'città' },
  { id: 'aosta', name: 'Aosta', lat: 45.7375, lng: 7.3154, type: 'città' },
  { id: 'courmayeur', name: 'Courmayeur', lat: 45.7967, lng: 6.9686, type: 'città' },
  { id: 'cervinia', name: 'Cervinia', lat: 45.9347, lng: 7.6319, type: 'città' },
  { id: 'pre_saint_didier', name: 'Pré-Saint-Didier', lat: 45.7614, lng: 6.9822, type: 'città' },
  { id: 'roma', name: 'Roma', lat: 41.9028, lng: 12.4964, type: 'città' },
  { id: 'firenze', name: 'Firenze', lat: 43.7696, lng: 11.2558, type: 'città' },
  { id: 'venezia', name: 'Venezia', lat: 45.4408, lng: 12.3155, type: 'città' },
  { id: 'bologna', name: 'Bologna', lat: 44.4949, lng: 11.3426, type: 'città' },
  { id: 'verona', name: 'Verona', lat: 45.4384, lng: 10.9916, type: 'città' },
  { id: 'brescia', name: 'Brescia', lat: 45.5416, lng: 10.2118, type: 'città' },
  { id: 'bergamo', name: 'Bergamo', lat: 45.6983, lng: 9.6773, type: 'città' },
  { id: 'monza', name: 'Monza', lat: 45.5845, lng: 9.2744, type: 'città' },
  { id: 'pavia', name: 'Pavia', lat: 45.1847, lng: 9.1582, type: 'città' },
  { id: 'cremona', name: 'Cremona', lat: 45.1336, lng: 10.0227, type: 'città' },
  { id: 'mantova', name: 'Mantova', lat: 45.1564, lng: 10.7914, type: 'città' },
  { id: 'piacenza', name: 'Piacenza', lat: 45.0526, lng: 9.6930, type: 'città' },
  { id: 'parma', name: 'Parma', lat: 44.8015, lng: 10.3279, type: 'città' },
  { id: 'modena', name: 'Modena', lat: 44.6471, lng: 10.9252, type: 'città' },
  { id: 'reggio_emilia', name: 'Reggio Emilia', lat: 44.6989, lng: 10.6297, type: 'città' },
  { id: 'novara', name: 'Novara', lat: 45.4469, lng: 8.6200, type: 'città' },
  { id: 'alessandria', name: 'Alessandria', lat: 44.9133, lng: 8.6153, type: 'città' },
  { id: 'asti', name: 'Asti', lat: 44.9007, lng: 8.2064, type: 'città' },
  { id: 'cuneo', name: 'Cuneo', lat: 44.3844, lng: 7.5427, type: 'città' },
  { id: 'alba', name: 'Alba', lat: 44.7000, lng: 8.0353, type: 'città' },
  { id: 'portofino', name: 'Portofino', lat: 44.3033, lng: 9.2097, type: 'città' },
  { id: 'santa_margherita', name: 'Santa Margherita Ligure', lat: 44.3344, lng: 9.2103, type: 'città' },
  { id: 'rapallo', name: 'Rapallo', lat: 44.3500, lng: 9.2333, type: 'città' },
  { id: 'sanremo', name: 'Sanremo', lat: 43.8175, lng: 7.7772, type: 'città' },
  { id: 'ventimiglia', name: 'Ventimiglia', lat: 43.7914, lng: 7.6078, type: 'città' },
  { id: 'livigno', name: 'Livigno', lat: 46.5389, lng: 10.1358, type: 'città' },
  { id: 'bormio', name: 'Bormio', lat: 46.4686, lng: 10.3708, type: 'città' },
  { id: 'sondrio', name: 'Sondrio', lat: 46.1699, lng: 9.8716, type: 'città' },
  { id: 'lecco', name: 'Lecco', lat: 45.8566, lng: 9.3977, type: 'città' },
  { id: 'lago_como', name: 'Lago di Como', lat: 45.8081, lng: 9.0852, type: 'città' },
  { id: 'lago_maggiore', name: 'Lago Maggiore', lat: 45.8833, lng: 8.5333, type: 'città' },
  { id: 'lago_orta', name: "Lago d'Orta", lat: 45.7969, lng: 8.4172, type: 'città' },
  { id: 'cinque_terre', name: 'Cinque Terre', lat: 44.0997, lng: 9.7375, type: 'città' },
  { id: 'monte_bianco', name: 'Monte Bianco', lat: 45.7967, lng: 6.9686, type: 'città' },
  { id: 'custom_itinerary', name: '📝 Itinerario personalizzato', lat: 0, lng: 0, type: 'custom' },
];

// Funzione per calcolare distanza in km (formula Haversine)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Raggio terra in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  // Aggiungi 20% per percorso stradale (non in linea d'aria)
  return Math.round(distance * 1.2);
};

// Booking Modal a Step
const BookingModal = ({ isOpen, onClose, lang, presetTo }) => {
  const t = bookingTranslations[lang] || bookingTranslations.IT;
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    serviceType: 'transfer',
    vehicleType: 'minivan',
    fromId: '',
    toId: '',
    fromText: '',
    toText: '',
    // Campi per indirizzo personalizzato partenza
    fromStreet: '',
    fromNumber: '',
    fromCity: '',
    // Campi per indirizzo personalizzato destinazione
    toStreet: '',
    toNumber: '',
    toCity: '',
    // Campi per itinerario completamente personalizzato
    customFromAddress: '',
    customToAddress: '',
    date: '',
    time: '',
    passengers: '1',
    km: '',
    hours: '4',
    name: '',
    surname: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  
  // State per Google Maps Distance Matrix
  const [googleKm, setGoogleKm] = useState(null);
  const [googleDuration, setGoogleDuration] = useState(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [distanceError, setDistanceError] = useState(null);
  const [addressWarning, setAddressWarning] = useState(null); // Avviso indirizzo non trovato
  
  // State per Google Places Autocomplete
  const [fromSearchQuery, setFromSearchQuery] = useState('');
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [isSearchingFrom, setIsSearchingFrom] = useState(false);
  const [isSearchingTo, setIsSearchingTo] = useState(false);
  const [selectedFromPlace, setSelectedFromPlace] = useState(null);
  const [selectedToPlace, setSelectedToPlace] = useState(null);

  // Pre-compila la destinazione quando il modale viene aperto da una card estiva
  useEffect(() => {
    if (isOpen && presetTo) {
      setFormData(prev => ({ ...prev, toText: presetTo.label, notes: presetTo.note || prev.notes }));
    }
  }, [isOpen, presetTo]);

  // Funzione per cercare nelle località predefinite
  const searchLocalLocations = (query) => {
    if (!query || query.length < 2) return [];
    
    const normalizedQuery = query.toLowerCase().trim();
    const words = normalizedQuery.split(/\s+/).filter(w => w.length > 1);
    
    // Parole chiave che indicano un tipo specifico
    const isSearchingStation = words.some(w => 
      w.includes('stazion') || w.includes('fs') || w.includes('treno') || w.includes('ferrovia')
    );
    const isSearchingAirport = words.some(w => 
      w.includes('aeroport') || w.includes('malpensa') || w.includes('linate') || w.includes('orio')
    );
    const isSearchingPort = words.some(w => 
      w.includes('porto') || w.includes('terminal') || w.includes('traghett') || w.includes('crocier')
    );
    
    // Parole da ignorare nella ricerca
    const stopWords = ['di', 'del', 'della', 'dei', 'delle', 'da', 'a', 'in', 'con', 'su', 'per', 'tra', 'fra'];
    const searchWords = words.filter(w => 
      !stopWords.includes(w) && 
      !['stazion', 'stazione', 'aeroporto', 'porto'].includes(w)
    );
    
    return LOCATIONS.filter(loc => {
      const locName = loc.name.toLowerCase();
      const locId = loc.id.toLowerCase();
      
      // Se sta cercando una stazione e questa location è una stazione
      if (isSearchingStation && loc.type === 'stazione') {
        // Cerca se almeno una parola significativa è nel nome
        const hasMatch = searchWords.some(word => 
          locName.includes(word) || locId.includes(word)
        );
        if (hasMatch || searchWords.length === 0) return true;
      }
      
      // Se sta cercando un aeroporto e questa location è un aeroporto
      if (isSearchingAirport && loc.type === 'aeroporto') {
        const hasMatch = searchWords.some(word => 
          locName.includes(word) || locId.includes(word)
        );
        if (hasMatch || searchWords.length === 0) return true;
      }
      
      // Se sta cercando un porto e questa location è un porto
      if (isSearchingPort && loc.type === 'porto') {
        const hasMatch = searchWords.some(word => 
          locName.includes(word) || locId.includes(word)
        );
        if (hasMatch || searchWords.length === 0) return true;
      }
      
      // Ricerca generica: cerca se tutte le parole sono contenute
      const allWordsMatch = words.every(word => 
        locName.includes(word) || locId.includes(word)
      );
      if (allWordsMatch) return true;
      
      // Ricerca flessibile: almeno una parola significativa è presente
      const anySignificantMatch = searchWords.some(word => 
        word.length >= 3 && (locName.includes(word) || locId.includes(word))
      );
      if (anySignificantMatch) return true;
      
      return false;
    }).slice(0, 8).map(loc => ({
      description: loc.name + ', Italia',
      place_id: loc.id,
      lat: loc.lat,
      lng: loc.lng,
      types: [loc.type],
      isLocal: true, // Flag per identificare località predefinite
      locationId: loc.id,
      locationType: loc.type // Tipo di località per l'icona
    }));
  };

  // Funzione per cercare luoghi con Google Places Autocomplete + località locali
  const searchPlaces = useCallback(async (query, setResults, setLoading) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    
    // Prima cerca nelle località predefinite
    const localResults = searchLocalLocations(query);
    
    // Se ci sono risultati locali, mostrali subito
    if (localResults.length > 0) {
      setResults(localResults);
    }
    
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      if (localResults.length === 0) setResults([]);
      return;
    }
    
    setLoading(true);
    
    try {
      // Usa Geocoding API per cercare indirizzi in Italia
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query + ', Italia')}&components=country:IT&language=it&key=${apiKey}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.status === 'OK' && data.results) {
        const googleSuggestions = data.results.slice(0, 5).map(result => ({
          description: result.formatted_address,
          place_id: result.place_id,
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng,
          types: result.types,
          isLocal: false
        }));
        
        // Combina risultati locali (prima) con Google (dopo), rimuovendo duplicati
        const combinedResults = [...localResults];
        googleSuggestions.forEach(gs => {
          if (!combinedResults.some(lr => lr.description.toLowerCase().includes(gs.description.toLowerCase().split(',')[0]))) {
            combinedResults.push(gs);
          }
        });
        
        setResults(combinedResults.slice(0, 8));
      } else if (localResults.length === 0) {
        setResults([]);
      }
    } catch (error) {
      console.error('Errore ricerca luoghi:', error);
      if (localResults.length === 0) setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce per la ricerca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (fromSearchQuery.length >= 2) {
        searchPlaces(fromSearchQuery, setFromSuggestions, setIsSearchingFrom);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fromSearchQuery, searchPlaces]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (toSearchQuery.length >= 2) {
        searchPlaces(toSearchQuery, setToSuggestions, setIsSearchingTo);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [toSearchQuery, searchPlaces]);

  // Gestione selezione luogo partenza
  const handleSelectFromPlace = (place) => {
    setSelectedFromPlace(place);
    setFromSearchQuery(place.description.split(',')[0]); // Mostra solo la parte principale
    setShowFromSuggestions(false);
    
    // Se è una località predefinita (stazione, aeroporto, porto), usa l'ID originale
    if (place.isLocal && place.locationId) {
      setFormData(prev => ({
        ...prev,
        fromId: place.locationId,
        fromCity: place.description,
        fromStreet: '',
        fromNumber: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        fromId: 'autocomplete',
        fromCity: place.description,
        fromStreet: '',
        fromNumber: ''
      }));
    }
  };

  // Gestione selezione luogo destinazione
  const handleSelectToPlace = (place) => {
    setSelectedToPlace(place);
    setToSearchQuery(place.description.split(',')[0]); // Mostra solo la parte principale
    setShowToSuggestions(false);
    
    // Se è una località predefinita (stazione, aeroporto, porto), usa l'ID originale
    if (place.isLocal && place.locationId) {
      setFormData(prev => ({
        ...prev,
        toId: place.locationId,
        toCity: place.description,
        toStreet: '',
        toNumber: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        toId: 'autocomplete',
        toCity: place.description,
        toStreet: '',
        toNumber: ''
      }));
    }
  };

  // Estrai il nome della città dalla località
  const getCityName = (locationId) => {
    const cityMap = {
      'milano': 'Milano',
      'milano_fiera': 'Milano',
      'varese': 'Varese',
      'busto_arsizio': 'Busto Arsizio',
      'gallarate': 'Gallarate',
      'saronno': 'Saronno',
      'vergiate': 'Vergiate',
      'como': 'Como',
      'cernobbio': 'Cernobbio',
      'bellagio': 'Bellagio',
      'torino': 'Torino',
      'genova': 'Genova',
      'stresa': 'Stresa',
      'arona': 'Arona',
      'lugano': 'Lugano',
      'aosta': 'Aosta',
      'courmayeur': 'Courmayeur',
      'cervinia': 'Cervinia',
      'somma_lombardo': 'Somma Lombardo',
      'pre_saint_didier': 'Pré-Saint-Didier',
    };
    return cityMap[locationId] || '';
  };

  // Funzione per costruire l'indirizzo completo per Google Maps
  const buildFullAddress = (id, street, number, city, selectedPlace) => {
    // Se è un luogo selezionato da autocomplete
    if (id === 'autocomplete' && selectedPlace) {
      // Se c'è via e numero, crea indirizzo completo con la descrizione del luogo
      if (street) {
        const streetPart = number ? `${street} ${number}` : street;
        // Usa la descrizione completa del luogo selezionato per maggiore precisione
        // Rimuovi solo "Italia" finale se presente per evitare duplicazioni
        let placeDesc = selectedPlace.description.replace(/, Italia$/i, '').trim();
        return `${streetPart}, ${placeDesc}, Italia`;
      }
      return selectedPlace.description;
    }
    
    if (id === 'custom_itinerary') {
      // Per itinerario personalizzato, costruisci indirizzo da campi
      const parts = [];
      if (street) parts.push(street);
      if (number) parts.push(number);
      if (city) parts.push(city);
      parts.push('Italia'); // Aggiungi sempre Italia per precisione
      return parts.join(', ');
    } else {
      // Per località predefinite
      const loc = LOCATIONS.find(l => l.id === id);
      if (!loc) return null;
      
      // Per aeroporti/stazioni/porti usa il nome diretto
      if (['aeroporto', 'stazione', 'porto'].includes(loc.type)) {
        return `${loc.name}, Italia`;
      }
      
      // Per città, aggiungi via se presente
      const parts = [];
      if (street) parts.push(street);
      if (number) parts.push(number);
      parts.push(loc.name);
      parts.push('Italia');
      return parts.join(', ');
    }
  };

  // Funzione per calcolare distanza con Google Maps Distance Matrix API (via backend)
  const calculateGoogleDistance = useCallback(async (originAddress, destinationAddress, fromPlace, toPlace, streetInfo) => {
    if (!originAddress || !destinationAddress) return;

    setIsCalculatingDistance(true);
    setDistanceError(null);
    setAddressWarning(null);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Costruisci il body con indirizzi e coordinate se disponibili
      const requestBody = {
        origin: originAddress,
        destination: destinationAddress
      };
      
      // Aggiungi coordinate di origine se disponibili (più precise)
      if (fromPlace && fromPlace.lat && fromPlace.lng) {
        requestBody.origin_lat = fromPlace.lat;
        requestBody.origin_lng = fromPlace.lng;
      }
      
      // Aggiungi coordinate di destinazione se disponibili
      if (toPlace && toPlace.lat && toPlace.lng) {
        requestBody.destination_lat = toPlace.lat;
        requestBody.destination_lng = toPlace.lng;
      }
      
      const response = await fetch(`${apiUrl}/api/distance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nel calcolo della distanza');
      }

      const data = await response.json();
      
      setGoogleKm(data.distance_km);
      setGoogleDuration(data.duration_minutes);
      
      // Controlla se la via inserita dall'utente appare nell'indirizzo risolto da Google
      const warnings = [];
      
      // Controlla origine - se l'utente ha inserito una via, verifica che appaia nella risposta
      if (streetInfo.fromStreet && streetInfo.fromStreet.trim().length > 3) {
        const userStreet = streetInfo.fromStreet.toLowerCase().replace(/via\s+/i, '').trim();
        const resolvedOrigin = data.origin_address.toLowerCase();
        
        // Estrai le parole chiave dalla via (es. "De Gasperi" da "Via Alcide De Gasperi")
        const streetWords = userStreet.split(/\s+/).filter(w => w.length > 2);
        const streetFoundInResolved = streetWords.some(word => resolvedOrigin.includes(word));
        
        if (!streetFoundInResolved) {
          warnings.push({
            type: 'origin',
            requested: `${streetInfo.fromStreet}${streetInfo.fromNumber ? ' ' + streetInfo.fromNumber : ''}, ${fromPlace?.description || streetInfo.fromCity || ''}`,
            resolved: data.origin_address
          });
        }
      }
      
      // Controlla destinazione
      if (streetInfo.toStreet && streetInfo.toStreet.trim().length > 3) {
        const userStreet = streetInfo.toStreet.toLowerCase().replace(/via\s+/i, '').trim();
        const resolvedDest = data.destination_address.toLowerCase();
        
        const streetWords = userStreet.split(/\s+/).filter(w => w.length > 2);
        const streetFoundInResolved = streetWords.some(word => resolvedDest.includes(word));
        
        if (!streetFoundInResolved) {
          warnings.push({
            type: 'destination',
            requested: `${streetInfo.toStreet}${streetInfo.toNumber ? ' ' + streetInfo.toNumber : ''}, ${toPlace?.description || streetInfo.toCity || ''}`,
            resolved: data.destination_address
          });
        }
      }
      
      if (warnings.length > 0) {
        setAddressWarning(warnings);
      }
      
    } catch (error) {
      console.error('Errore calcolo distanza Google:', error);
      setDistanceError(error.message || 'Errore nel calcolo della distanza');
      setGoogleKm(null);
      setGoogleDuration(null);
    } finally {
      setIsCalculatingDistance(false);
    }
  }, []);

  // Effect per calcolare distanza quando cambiano gli indirizzi
  useEffect(() => {
    const originAddress = buildFullAddress(formData.fromId, formData.fromStreet, formData.fromNumber, formData.fromCity, selectedFromPlace);
    const destAddress = buildFullAddress(formData.toId, formData.toStreet, formData.toNumber, formData.toCity, selectedToPlace);
    
    if (originAddress && destAddress) {
      // Debounce per evitare troppe chiamate API
      const timeoutId = setTimeout(() => {
        const streetInfo = {
          fromStreet: formData.fromStreet,
          fromNumber: formData.fromNumber,
          fromCity: formData.fromCity,
          toStreet: formData.toStreet,
          toNumber: formData.toNumber,
          toCity: formData.toCity
        };
        calculateGoogleDistance(originAddress, destAddress, selectedFromPlace, selectedToPlace, streetInfo);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setGoogleKm(null);
      setGoogleDuration(null);
      setAddressWarning(null);
    }
  }, [formData.fromId, formData.toId, formData.fromStreet, formData.fromNumber, formData.fromCity, formData.toStreet, formData.toNumber, formData.toCity, selectedFromPlace, selectedToPlace, calculateGoogleDistance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updates = { [name]: value };
    
    // Precompila città quando si seleziona una località di tipo "città" o "fiera"
    if (name === 'fromId') {
      const loc = LOCATIONS.find(l => l.id === value);
      if (loc && (loc.type === 'città' || loc.type === 'fiera')) {
        updates.fromCity = getCityName(value);
      } else {
        updates.fromCity = '';
        updates.fromStreet = '';
        updates.fromNumber = '';
      }
      // Reset Google km quando cambia località
      setGoogleKm(null);
      setGoogleDuration(null);
    }
    
    if (name === 'toId') {
      const loc = LOCATIONS.find(l => l.id === value);
      if (loc && (loc.type === 'città' || loc.type === 'fiera')) {
        updates.toCity = getCityName(value);
      } else {
        updates.toCity = '';
        updates.toStreet = '';
        updates.toNumber = '';
      }
      // Reset Google km quando cambia località
      setGoogleKm(null);
      setGoogleDuration(null);
    }
    
    setFormData({ ...formData, ...updates });
  };

  // Funzione per trovare coordinate da nome città
  const findCityCoordinates = (cityName) => {
    if (!cityName) return null;
    const normalizedName = cityName.toLowerCase().trim();
    // Cerca corrispondenza esatta o parziale
    const found = LOCATIONS.find(l => 
      l.name.toLowerCase() === normalizedName ||
      l.name.toLowerCase().includes(normalizedName) ||
      normalizedName.includes(l.name.toLowerCase())
    );
    return found ? { lat: found.lat, lng: found.lng } : null;
  };

  // Calcola km automaticamente quando cambiano partenza/arrivo
  const getCalculatedKm = () => {
    let fromCoords = null;
    let toCoords = null;

    // Per la partenza
    if (formData.fromId === 'custom_itinerary') {
      // Cerca le coordinate dalla città inserita
      fromCoords = findCityCoordinates(formData.fromCity);
    } else {
      const fromLoc = LOCATIONS.find(l => l.id === formData.fromId);
      if (fromLoc && fromLoc.lat !== 0) {
        fromCoords = { lat: fromLoc.lat, lng: fromLoc.lng };
      }
    }

    // Per la destinazione
    if (formData.toId === 'custom_itinerary') {
      // Cerca le coordinate dalla città inserita
      toCoords = findCityCoordinates(formData.toCity);
    } else {
      const toLoc = LOCATIONS.find(l => l.id === formData.toId);
      if (toLoc && toLoc.lat !== 0) {
        toCoords = { lat: toLoc.lat, lng: toLoc.lng };
      }
    }

    // Calcola distanza se abbiamo entrambe le coordinate
    // Se abbiamo il calcolo Google, usalo
    if (googleKm) {
      return { auto: googleKm, manual: false, isGoogle: true };
    }
    
    // Altrimenti usa le coordinate locali
    if (fromCoords && toCoords) {
      return { auto: calculateDistance(fromCoords.lat, fromCoords.lng, toCoords.lat, toCoords.lng), manual: false, isGoogle: false };
    }
    
    // Restituisci i km manuali se presenti
    return { auto: 0, manual: formData.km ? parseInt(formData.km) : 0, isGoogle: false };
  };

  const kmResult = getCalculatedKm();
  const autoCalculatedKm = kmResult.auto; // Km calcolati automaticamente
  const calculatedKm = kmResult.auto || kmResult.manual; // Km totali (auto o manuali)
  const isGoogleCalculated = kmResult.isGoogle; // Flag per sapere se è da Google

  // Nome località per visualizzazione
  const getLocationName = (id, street, number, city) => {
    // Se è itinerario personalizzato, mostra città + via + numero
    if (id === 'custom_itinerary') {
      if (city && street && number) {
        return `${street} ${number}, ${city}`;
      } else if (city && street) {
        return `${street}, ${city}`;
      } else if (city) {
        return city;
      }
      return 'Indirizzo personalizzato';
    }
    
    const loc = LOCATIONS.find(l => l.id === id);
    const baseName = loc ? loc.name : '';
    
    // Se c'è via e numero, aggiungi all'indirizzo
    if (street) {
      const fullAddress = `${street}${number ? ' ' + number : ''}${city ? ', ' + city : ''}`;
      return baseName ? `${baseName} - ${fullAddress}` : fullAddress;
    }
    return baseName || 'Indirizzo non specificato';
  };

  // Calcola il preventivo
  const calculatePrice = () => {
    const { serviceType, vehicleType, hours } = formData;
    const kmNum = calculatedKm || parseFloat(formData.km) || 0;
    const hoursNum = parseFloat(hours) || 4;

    // TARIFFA MINIMA: €40 per tratte sotto i 15 km
    const TARIFFA_MINIMA = 70;
    const KM_MINIMO = 15;

    if (serviceType === 'transfer') {
      // Trasferimento: prezzo = km * tariffa/km
      const tariffa = TARIFFE.transfer[vehicleType];
      const prezzoCalcolato = kmNum * tariffa;
      
      // Se sotto i 15 km, applica tariffa minima di €40
      if (kmNum < KM_MINIMO && kmNum > 0) {
        return Math.max(prezzoCalcolato, TARIFFA_MINIMA);
      }
      return prezzoCalcolato;
    } else {
      // A disposizione
      const tariffe = TARIFFE.disposizione[vehicleType];
      let totale = tariffe.base; // Base 4 ore, 80 km inclusi
      
      // Ore extra
      if (hoursNum > TARIFFE.oreMinime) {
        totale += (hoursNum - TARIFFE.oreMinime) * tariffe.oraExtra;
      }
      
      // Km extra
      if (kmNum > TARIFFE.kmInclusi) {
        totale += (kmNum - TARIFFE.kmInclusi) * tariffe.kmExtra;
      }
      
      return totale;
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      if (currentStep === 3) {
        setShowPrice(true);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 4) {
        setShowPrice(false);
      }
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.serviceType && formData.vehicleType;
      case 2:
        return formData.fromText && formData.toText && formData.date && formData.time;
      case 3:
        return formData.name && formData.surname && formData.phone && formData.email;
      default:
        return true;
    }
  };

  const fromName = formData.fromText;
  const toName = formData.toText;

  const handleSubmitEmail = () => {
    setIsSubmitting(true);
    const vehicleNames = { berlina: 'Berlina (Maserati)', minivan: 'Minivan', minibus: 'Minibus VIP' };
    const serviceNames = { transfer: 'Trasferimento', disposizione: 'A Disposizione' };
    
    const subject = `Richiesta Preventivo NCC - ${formData.name} ${formData.surname}`;
    const body = `Richiesta Preventivo NCC Alex Tours Transfer

DETTAGLI SERVIZIO:
- Tipo Servizio: ${serviceNames[formData.serviceType]}
- Veicolo: ${vehicleNames[formData.vehicleType]}
- Da: ${fromName}
- A: ${toName}
- Data: ${formData.date}
- Ora: ${formData.time}
- Km stimati: ${calculatedKm || formData.km} km
${formData.serviceType === 'disposizione' ? `- Ore: ${formData.hours}` : ''}
- Passeggeri: ${formData.passengers}

DATI CLIENTE:
- Nome: ${formData.name} ${formData.surname}
- Telefono: ${formData.phone}
- Email: ${formData.email}
${formData.notes ? `- Note: ${formData.notes}` : ''}

---
Richiesta inviata tramite www.alexncc.com`;
    
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    window.open(`mailto:prenotazioni.alexncc@gmail.com?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
    
    setTimeout(() => {
      setIsSubmitting(false);
      onClose();
      setCurrentStep(1);
      setShowPrice(false);
    }, 1000);
  };

  const resetAndClose = () => {
    onClose();
    setCurrentStep(1);
    setShowPrice(false);
    setFormData({
      serviceType: 'transfer',
      vehicleType: 'minivan',
      fromId: '',
      toId: '',
      fromStreet: '',
      fromNumber: '',
      fromCity: '',
      toStreet: '',
      toNumber: '',
      toCity: '',
      date: '',
      time: '',
      passengers: '1',
      km: '',
      hours: '4',
      name: '',
      surname: '',
      phone: '',
      email: '',
      notes: '',
    });
  };

  if (!isOpen) return null;

  const steps = [
    { num: 1, label: t.step1 },
    { num: 2, label: t.step2 },
    { num: 3, label: t.step3 },
    { num: 4, label: t.step4 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={resetAndClose} />
      
      <div className="relative bg-slate-800 rounded-2xl sm:rounded-3xl w-full max-w-xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl">
        {/* Close button */}
        <button onClick={resetAndClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 text-slate-400 hover:text-white transition-colors p-1">
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Header con Step Indicator */}
        <div className="bg-slate-900 p-3 sm:p-4 md:p-6 border-b border-slate-700">
          <h2 className="text-base sm:text-lg md:text-xl font-bold text-white text-center mb-3 sm:mb-4 pr-6">{t.title}</h2>
          
          {/* Step Indicator - Ottimizzato per mobile */}
          <div className="flex justify-between items-center">
            {steps.map((step, i) => (
              <div key={step.num} className="flex items-center">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm transition-all ${
                  currentStep >= step.num 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-slate-700 text-slate-400'
                }`}>
                  {currentStep > step.num ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" /> : step.num}
                </div>
                <span className={`ml-1 sm:ml-2 text-[10px] sm:text-xs hidden sm:block ${
                  currentStep >= step.num ? 'text-cyan-400' : 'text-slate-500'
                }`}>{step.label}</span>
                {i < steps.length - 1 && (
                  <div className={`w-4 sm:w-6 md:w-12 h-0.5 sm:h-1 mx-1 sm:mx-2 rounded ${
                    currentStep > step.num ? 'bg-cyan-500' : 'bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-6 overflow-y-auto max-h-[55vh] sm:max-h-[60vh]">
          
          {/* STEP 1: Servizio e Veicolo */}
          {currentStep === 1 && (
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2 sm:mb-3">{t.serviceType}</label>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, serviceType: 'transfer' })}
                    className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all text-left ${
                      formData.serviceType === 'transfer'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Plane className={`w-5 h-5 sm:w-6 sm:h-6 mb-1.5 sm:mb-2 ${formData.serviceType === 'transfer' ? 'text-cyan-400' : 'text-slate-400'}`} />
                    <div className={`font-semibold text-sm sm:text-base ${formData.serviceType === 'transfer' ? 'text-white' : 'text-slate-300'}`}>{t.transfer}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">{t.transferDesc}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, serviceType: 'disposizione' })}
                    className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all text-left ${
                      formData.serviceType === 'disposizione'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Clock className={`w-5 h-5 sm:w-6 sm:h-6 mb-1.5 sm:mb-2 ${formData.serviceType === 'disposizione' ? 'text-cyan-400' : 'text-slate-400'}`} />
                    <div className={`font-semibold text-sm sm:text-base ${formData.serviceType === 'disposizione' ? 'text-white' : 'text-slate-300'}`}>{t.rental}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">{t.rentalDesc}</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2 sm:mb-3">{t.selectVehicle}</label>
                <div className="space-y-2 sm:space-y-3">
                  {[
                    { id: 'minivan', name: t.minivan, desc: t.minivanDesc, icon: '🚐' },
                    { id: 'berlina', name: t.berlina, desc: t.berlinaDesc, icon: '🚗', premium: true },
                    { id: 'minibus', name: t.minibus, desc: t.minibusDesc, icon: '🚌' },
                  ].map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, vehicleType: v.id })}
                      className={`w-full p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all flex items-center gap-2 sm:gap-4 ${
                        formData.vehicleType === v.id
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <span className="text-2xl sm:text-3xl">{v.icon}</span>
                      <div className="text-left flex-1 min-w-0">
                        <div className={`font-semibold text-sm sm:text-base flex items-center gap-1 sm:gap-2 flex-wrap ${formData.vehicleType === v.id ? 'text-white' : 'text-slate-300'}`}>
                          {v.name}
                          {v.premium && <span className="text-[10px] sm:text-xs bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full">PREMIUM</span>}
                        </div>
                        <div className="text-[10px] sm:text-xs text-slate-500 truncate">{v.desc}</div>
                      </div>
                      {formData.vehicleType === v.id && <Check className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Dettagli Viaggio */}
          {currentStep === 2 && (
            <div className="space-y-3 sm:space-y-4">
              {/* Partenza - indirizzo libero */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">📍 {t.from}</label>
                <input
                  type="text"
                  name="fromText"
                  value={formData.fromText}
                  onChange={handleChange}
                  placeholder={t.customFromPlaceholder}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                />
              </div>

              {/* Destinazione - indirizzo libero */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">📍 {t.to}</label>
                <input
                  type="text"
                  name="toText"
                  value={formData.toText}
                  onChange={handleChange}
                  placeholder={t.customToPlaceholder}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">📅 {t.date}</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-2 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">🕐 {t.time}</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full px-2 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">👥 {t.passengers}</label>
                <select
                  name="passengers"
                  value={formData.passengers}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? t.passengerSingular : t.passengerPlural}</option>
                  ))}
                </select>
              </div>

              {formData.serviceType === 'disposizione' && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">⏱️ {t.totalHours}</label>
                  <select
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                  >
                    {[4,5,6,7,8,9,10,11,12].map(n => (
                      <option key={n} value={n}>{n} {t.hours} {n === 4 ? '(min)' : ''}</option>
                    ))}
                  </select>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2">{t.minHoursInfo || 'Minimo 4 ore con 80 km inclusi'}</p>
                </div>
              )}

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">📝 {t.notes}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t.notesPlaceholder}
                  rows={2}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500 resize-none"
                />
              </div>
            </div>
          )}

          {/* STEP 3: Dati Personali */}
          {currentStep === 3 && (
            <div className="space-y-3 sm:space-y-4">
              <p className="text-slate-400 text-xs sm:text-sm text-center mb-3 sm:mb-4">
                {t.enterData}
              </p>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">{t.name} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.namePlaceholder}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">{t.surname} *</label>
                  <input
                    type="text"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    placeholder={t.surnamePlaceholder}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">{t.phone} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.phonePlaceholder}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">{t.email} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.emailPlaceholder}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>
          )}

          {/* STEP 4: Riepilogo e Preventivo */}
          {currentStep === 4 && showPrice && (
            <div className="space-y-3 sm:space-y-4">
              {/* Riepilogo */}
              <div className="bg-slate-700/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 space-y-2 sm:space-y-3">
                <h3 className="font-semibold text-white text-sm sm:text-base mb-2 sm:mb-3">{t.summary}</h3>
                
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">{t.service}:</span>
                  <span className="text-white">{formData.serviceType === 'transfer' ? t.transfer : t.rental}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">{t.vehicle}:</span>
                  <span className="text-white">
                    {formData.vehicleType === 'berlina' ? t.berlina : 
                     formData.vehicleType === 'minivan' ? t.minivan : t.minibus}
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400 flex-shrink-0">{t.route}:</span>
                  <span className="text-white text-right max-w-[150px] sm:max-w-[200px] truncate">{fromName} → {toName}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">{t.dateTime}:</span>
                  <span className="text-white">{formData.date} {t.at} {formData.time}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">{t.estimatedKmLabel}:</span>
                  <span className="text-white">{calculatedKm || formData.km} km</span>
                </div>
                {formData.serviceType === 'disposizione' && (
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-slate-400">{t.hours}:</span>
                    <span className="text-white">{formData.hours} {t.hours}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">{t.passengers}:</span>
                  <span className="text-white">{formData.passengers}</span>
                </div>
                
                <div className="border-t border-slate-600 pt-2 sm:pt-3 mt-2 sm:mt-3">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-slate-400">{t.client}:</span>
                    <span className="text-white truncate max-w-[150px] sm:max-w-[200px]">{formData.name} {formData.surname}</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-slate-400">{t.phone}:</span>
                    <span className="text-white">{formData.phone}</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-slate-400">{t.email}:</span>
                    <span className="text-white truncate max-w-[150px] sm:max-w-[200px]">{formData.email}</span>
                  </div>
                </div>
              </div>

              {/* Messaggio preventivo gratuito */}
              <div className="bg-gradient-to-r from-cyan-500/20 to-amber-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-cyan-500/30 text-center">
                <p className="text-sm sm:text-base font-semibold text-white">{t.quoteFreeTitle}</p>
                <p className="text-xs sm:text-sm text-slate-300 mt-1.5 sm:mt-2">
                  {t.quoteDisclaimer}
                </p>
              </div>

              {/* Scelta metodo invio */}
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-600">
                <p className="text-xs sm:text-sm text-slate-300 text-center mb-3 sm:mb-4">{t.chooseSubmitMethod}</p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleSubmitEmail}
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 disabled:bg-slate-600 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl transition-all hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base shadow-lg shadow-cyan-500/20"
                    data-testid="submit-email-btn"
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                        {t.sendEmail}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer con bottoni navigazione */}
        <div className="p-3 sm:p-4 md:p-6 border-t border-slate-700 bg-slate-900">
          <div className="flex gap-2 sm:gap-3">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl transition-colors text-sm sm:text-base"
              >
                {t.back}
              </button>
            )}
            
            {currentStep < 4 && (
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl transition-colors flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
              >
                {currentStep === 3 ? t.seeQuote : t.continue}
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Vehicle Detail Modal
const VehicleDetailModal = ({ isOpen, onClose, vehicleName }) => {
  const vehicle = vehicleDetails[vehicleName];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when vehicle changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [vehicleName]);

  if (!isOpen || !vehicle) return null;

  const images = vehicle.images || [vehicle.image];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-slate-800 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-slate-900/80 text-white hover:bg-slate-700 transition-colors p-2 rounded-full"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Image Gallery */}
        <div className="relative h-64 md:h-80 overflow-hidden rounded-t-3xl">
          <img 
            src={images[currentImageIndex]} 
            alt={vehicle.name} 
            className="w-full h-full object-cover"
          />
          {/* Logo Watermark */}
          <img 
            src={LOGO_URL} 
            alt="" 
            className="absolute bottom-3 right-3 h-8 md:h-10 w-auto opacity-50"
          />
          {vehicle.premium && (
            <span className="absolute top-4 left-4 bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold">
              ✦ PREMIUM
            </span>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImageIndex(i)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    i === currentImageIndex ? 'bg-cyan-400' : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Header */}
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{vehicle.name}</h2>
          {vehicle.tagline && (
            <p className="text-cyan-400 text-lg mb-4">{vehicle.tagline}</p>
          )}
          {vehicle.description && (
            <p className="text-slate-400 mb-6">{vehicle.description}</p>
          )}

          {/* Versions (for Ford and Opel) */}
          {vehicle.versions && (
            <div className="space-y-4 mb-6">
              {vehicle.versions.map((version, i) => (
                <div key={i} className="bg-slate-700/50 rounded-2xl p-4">
                  <h3 className="text-lg font-bold text-white">{version.title}</h3>
                  <p className="text-cyan-400 text-sm mb-2">{version.subtitle}</p>
                  <p className="text-slate-400 text-sm">{version.desc}</p>
                </div>
              ))}
            </div>
          )}

          {/* Highlights */}
          {vehicle.highlights && (
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {vehicle.highlights.map((h, i) => (
                <div key={i} className="bg-slate-700/30 rounded-xl p-4 flex items-start gap-3">
                  <span className="text-2xl">{h.icon}</span>
                  <div>
                    <h4 className="text-white font-medium">{h.title}</h4>
                    <p className="text-slate-400 text-sm">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Features */}
          {vehicle.features && (
            <div className="flex flex-wrap gap-3 mb-6">
              {vehicle.features.map((f, i) => (
                <div key={i} className="bg-slate-700/50 rounded-full px-4 py-2 flex items-center gap-2">
                  <span>{f.icon}</span>
                  <span className="text-slate-300 text-sm">{f.title}</span>
                </div>
              ))}
            </div>
          )}

          {/* Use Cases */}
          {vehicle.useCases && (
            <div className="flex flex-wrap gap-2">
              {vehicle.useCases.map((uc, i) => (
                <span key={i} className="bg-cyan-500/20 text-cyan-400 px-4 py-2 rounded-full text-sm">
                  {uc}
                </span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={`mailto:prenotazioni.alexncc@gmail.com?subject=${encodeURIComponent('Prenotazione ' + vehicle.name)}`}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white font-semibold py-3 rounded-xl transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Prenota via Email
            </a>
            <a
              href="tel:+393400859327"
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Chiama Ora
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cookie Banner
const CookieBanner = ({ t, onAccept, onReject }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 p-4 z-50">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full border border-slate-600 flex items-center justify-center">
          <Clock className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <h4 className="text-white font-medium">{t.cookie.title}</h4>
          <p className="text-slate-400 text-sm">
            {t.cookie.desc}{' '}
            <button 
              onClick={() => document.getElementById('privacy-modal').showModal()} 
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              {t.cookie.privacy || 'Privacy Policy'}
            </button>
          </p>
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onReject} className="px-6 py-2 rounded-full border border-slate-600 text-white hover:bg-slate-800 transition-colors">
          {t.cookie.reject}
        </button>
        <button onClick={onAccept} className="px-6 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors">
          {t.cookie.accept}
        </button>
      </div>
    </div>
  </div>
);

// Header con barra lingua
const Header = ({ lang, setLang, t, onBookClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <img 
          src={LOGO_URL} 
          alt="Alex Tours Transfer" 
          className="h-20 md:h-24 lg:h-28 w-auto object-contain"
        />
        
        {/* Language Selector - Centrato */}
        <div className="hidden md:flex items-center gap-1 bg-slate-800/80 backdrop-blur-sm rounded-full p-1">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                lang === l.code ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`lang-${l.code}`}
            >
              {l.code}
            </button>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={onBookClick}
          className="hidden md:flex bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-6 py-2.5 rounded-full font-medium transition-all hover:scale-105 shadow-lg shadow-cyan-500/20 btn-ripple"
          data-testid="book-button"
        >
          {t.nav.book}
        </button>

        {/* Mobile Menu Button */}
        <button className="md:hidden text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 backdrop-blur-sm border-t border-slate-800">
          <div className="p-4 space-y-4">
            {/* Mobile Language Selector */}
            <div className="flex flex-wrap justify-center gap-2 pb-4 border-b border-slate-700">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setMobileMenuOpen(false); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    lang === l.code ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  {l.code}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setMobileMenuOpen(false); onBookClick(); }}
              className="block w-full bg-gradient-to-r from-cyan-500 to-amber-500 text-white px-6 py-3 rounded-full font-medium text-center shadow-lg shadow-cyan-500/20"
            >
              {t.nav.book}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// Hero Section - Design originale (ottimizzato mobile)
const HeroSection = ({ t, onQuoteClick }) => (
  <section className="relative min-h-[85vh] sm:min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src={IMAGES.hero} alt="Luxury Car" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/40 to-transparent sm:from-slate-900/60 sm:via-slate-900/30 sm:to-transparent" />
      {/* Logo Watermark */}
      <img 
        src={LOGO_URL} 
        alt="" 
        className="absolute bottom-4 right-4 h-10 sm:h-12 md:h-14 w-auto opacity-40"
      />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24 md:py-32 w-full">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-1 sm:mb-2 opacity-0 animate-fade-in-up drop-shadow-lg">
          {t.hero.title}
        </h1>
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-cyan-400 mb-4 sm:mb-6 opacity-0 animate-fade-in-up delay-200 drop-shadow-lg">
          {t.hero.subtitle}
        </h2>
        <p className="text-white/90 text-base sm:text-lg md:text-xl mb-0.5 sm:mb-1 opacity-0 animate-fade-in-up delay-300 drop-shadow-md">{t.hero.desc1}</p>
        <p className="text-cyan-300 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-0 animate-fade-in-up delay-400 drop-shadow-md">{t.hero.desc2}</p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10 opacity-0 animate-fade-in-up delay-500">
          <button
            onClick={onQuoteClick}
            className="group bg-white hover:bg-slate-100 text-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all flex items-center justify-center gap-2 hover:scale-105"
            data-testid="quote-button"
          >
            {t.hero.cta1}
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href="#veicoli"
            className="bg-slate-800/70 backdrop-blur-sm hover:bg-slate-700/70 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all flex items-center justify-center gap-2 hover:scale-105"
          >
            {t.hero.cta2}
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 animate-bounce" />
          </a>
        </div>

        <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 text-slate-400 text-xs sm:text-sm opacity-0 animate-fade-in-up delay-600">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {t.hero.available}
          </div>
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t.hero.fleet}
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            {t.hero.rates}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// About Section con statistiche animate
const AboutSection = ({ t }) => (
  <section className="py-12 sm:py-16 md:py-24 bg-slate-900" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      {/* Stats bar - Numeri animati */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-12 sm:mb-16 md:mb-20">
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/50 rounded-xl sm:rounded-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-400 mb-1 sm:mb-2">
            <AnimatedNumber value={2500} suffix="+" duration={2000} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.about.stat1Label}</div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/50 rounded-xl sm:rounded-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-400 mb-1 sm:mb-2">
            <AnimatedNumber value={15000} suffix="+" duration={2500} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.about.stat2Label}</div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/50 rounded-xl sm:rounded-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-400 mb-1 sm:mb-2">
            <AnimatedNumber value={4.9} suffix="/5" duration={1500} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm flex items-center justify-center gap-1">
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" /> Google
          </div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/50 rounded-xl sm:rounded-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-400 mb-1 sm:mb-2">
            <AnimatedNumber value={10} suffix="+" duration={1500} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.about.years}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
        <div className="relative img-zoom order-2 md:order-1">
          <img src={IMAGES.about} alt="Mercedes" className="rounded-xl sm:rounded-2xl shadow-2xl w-full hover-lift" />
          {/* Logo Watermark */}
          <img 
            src={LOGO_URL} 
            alt="" 
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 h-8 sm:h-10 w-auto opacity-50"
          />
          <div className="absolute -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl animate-float">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold">100%</div>
            <div className="text-xs sm:text-sm">{t.about.punctuality}</div>
          </div>
        </div>

        <div className="order-1 md:order-2">
          <span className="inline-block bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
            {t.about.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">{t.about.title1}</h2>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4 sm:mb-6">{t.about.title2}</h3>
          <p className="text-slate-400 mb-3 sm:mb-4 text-base sm:text-lg">{t.about.desc1}</p>
          <p className="text-slate-400 mb-6 sm:mb-8 text-base sm:text-lg">
            {t.about.desc2} <span className="text-white font-semibold">{t.about.highlight}</span>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {t.about.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-colors flex-shrink-0">
                  {i === 0 && <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />}
                  {i === 1 && <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />}
                  {i === 2 && <Star className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />}
                  {i === 3 && <Award className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />}
                </div>
                <span className="text-white text-xs sm:text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section - con animazioni
const ServicesSection = ({ t }) => (
  <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <span className="inline-block bg-slate-700 text-cyan-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4 animate-pulse">
          ● {t.services.badge}
        </span>
        <h2 className="text-3xl md:text-5xl font-bold text-cyan-400 italic mb-4">{t.services.title}</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">{t.services.subtitle}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-slate-800/50 rounded-3xl overflow-hidden group hover:bg-slate-800/70 transition-all elegant-card">
          <div className="relative h-64 overflow-hidden img-zoom">
            <img src={IMAGES.airport} alt={t.services.airport.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
            {/* Logo Watermark */}
            <img src={LOGO_URL} alt="" className="absolute bottom-3 right-3 h-8 w-auto opacity-50 z-10" />
            <span className="absolute top-4 left-4 bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 subtle-float">
              <Plane className="w-4 h-4" />
              {t.services.airport.badge}
            </span>
          </div>
          <div className="p-6">
            <h3 className="text-2xl font-bold text-white mb-3">{t.services.airport.title}</h3>
            <p className="text-slate-400 mb-4">{t.services.airport.desc}</p>
            <ul className="space-y-2">
              {t.services.airport.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300 stagger-item">
                  <Check className="w-4 h-4 text-cyan-400" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-3xl overflow-hidden group hover:bg-slate-800/70 transition-all elegant-card">
          <div className="relative h-64 overflow-hidden img-zoom">
            <img src={IMAGES.chauffeur} alt={t.services.rental.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
            {/* Logo Watermark */}
            <img src={LOGO_URL} alt="" className="absolute bottom-3 right-3 h-8 w-auto opacity-50 z-10" />
            <span className="absolute top-4 left-4 bg-slate-600 text-white px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 subtle-float">
              <Clock className="w-4 h-4" />
              {t.services.rental.badge}
            </span>
          </div>
          <div className="p-6">
            <h3 className="text-2xl font-bold text-white mb-3">{t.services.rental.title}</h3>
            <p className="text-slate-400 mb-4">{t.services.rental.desc}</p>
            <ul className="space-y-2">
              {t.services.rental.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300 stagger-item">
                  <Check className="w-4 h-4 text-cyan-400" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Wedding Section - Matrimoni & Cerimonie
const WeddingSection = ({ t, onQuoteClick }) => {
  // Immagini wedding stagionali
  const weddingImages = [
    "https://static.prod-images.emergentagent.com/jobs/14b12a0d-1b64-432c-bf1d-e8eea1548cf9/images/450e6c00af435b985e041df6844ef5bd947e059290cfab9f9641b194980a1949.png", // Primavera
    "https://static.prod-images.emergentagent.com/jobs/14b12a0d-1b64-432c-bf1d-e8eea1548cf9/images/489f8b5a8e6cbb695f9bf5ce0db366ffc46c7805fe3be8d1f28faecb4805449d.png", // Estate
    "https://static.prod-images.emergentagent.com/jobs/14b12a0d-1b64-432c-bf1d-e8eea1548cf9/images/f47609620777d4cda31173fc67306293217e6911c991dcd23c5b0596c4c52fdd.png", // Autunno
    "https://static.prod-images.emergentagent.com/jobs/14b12a0d-1b64-432c-bf1d-e8eea1548cf9/images/5e42e2d05368f88aca4a04154168f6c798fa59eaee6af58964096c562ad0a6eb.png", // Inverno
  ];

  const wedding = t.wedding || {
    badge: "MATRIMONI & CERIMONIE",
    title: "Il Giorno Più Bello",
    titleHighlight: "Merita il Meglio",
    subtitle: "Trasformiamo il vostro giorno speciale in un'esperienza indimenticabile.",
    sposi: { title: "Trasporto Sposi", desc: "Maserati Quattroporte per un arrivo da sogno.", features: ["Auto decorata", "Champagne a bordo", "Autista in livrea"] },
    invitati: { title: "Trasporto Invitati", desc: "Minivan e Minibus VIP per tutti i vostri ospiti.", features: ["Fino a 20 passeggeri", "Coordinamento orari", "Servizio navetta"] },
    kit: { title: "Welcome Kit", desc: "Plaid personalizzati, lucine, champagne.", features: ["Plaid personalizzati", "Lucine romantiche", "Snack premium"] },
    gallery: "I Nostri Allestimenti",
    cta: "Richiedi Preventivo Matrimonio",
    ctaDesc: "Raccontaci il vostro giorno speciale.",
  };

  return (
    <section id="matrimoni" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden" data-testid="wedding-section">
      {/* Background decoration - romantic theme */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-pink-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-rose-500 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-400/30 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-500/30 text-pink-300 px-5 py-2 rounded-full text-sm font-medium mb-4 animate-pulse">
            💒 {wedding.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
            {wedding.title} <span className="text-pink-400">{wedding.titleHighlight}</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">{wedding.subtitle}</p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {/* Trasporto Sposi */}
          <div className="bg-gradient-to-br from-pink-500/10 via-slate-800/80 to-slate-800/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-pink-500/20 hover:border-pink-500/40 transition-all hover:shadow-lg hover:shadow-pink-500/10 group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="text-2xl">💍</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{wedding.sposi.title}</h3>
            <p className="text-slate-400 mb-5 text-sm sm:text-base">{wedding.sposi.desc}</p>
            <ul className="space-y-2">
              {wedding.sposi.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-pink-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Trasporto Invitati */}
          <div className="bg-gradient-to-br from-rose-500/10 via-slate-800/80 to-slate-800/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-rose-500/20 hover:border-rose-500/40 transition-all hover:shadow-lg hover:shadow-rose-500/10 group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="text-2xl">👥</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{wedding.invitati.title}</h3>
            <p className="text-slate-400 mb-5 text-sm sm:text-base">{wedding.invitati.desc}</p>
            <ul className="space-y-2">
              {wedding.invitati.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Welcome Kit */}
          <div className="bg-gradient-to-br from-amber-500/10 via-slate-800/80 to-slate-800/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-500/20 hover:border-amber-500/40 transition-all hover:shadow-lg hover:shadow-amber-500/10 group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="text-2xl">🎁</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{wedding.kit.title}</h3>
            <p className="text-slate-400 mb-5 text-sm sm:text-base">{wedding.kit.desc}</p>
            <ul className="space-y-2">
              {wedding.kit.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Gallery */}
        <div className="mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6 sm:mb-8">{wedding.gallery}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {weddingImages.map((img, i) => (
              <div key={i} className="relative rounded-xl sm:rounded-2xl overflow-hidden group cursor-pointer hover-lift aspect-[4/3]">
                <img 
                  src={img} 
                  alt={`Wedding setup ${i + 1}`} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {/* Logo Watermark */}
                <img 
                  src={LOGO_URL} 
                  alt="" 
                  className="absolute bottom-2 right-2 h-6 sm:h-8 w-auto opacity-50 z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-pink-500/10 via-rose-500/10 to-amber-500/10 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center border border-pink-500/20 hover:border-pink-500/30 transition-all">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">💒 {wedding.cta.replace('Richiedi Preventivo Matrimonio', '').trim() || 'Organizziamo Insieme il Vostro Giorno Speciale'}</h3>
          <p className="text-slate-400 mb-5 sm:mb-6 max-w-2xl mx-auto text-sm sm:text-base">{wedding.ctaDesc}</p>
          <button 
            onClick={onQuoteClick}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg shadow-pink-500/30 text-sm sm:text-base"
          >
            <span>💍</span>
            {wedding.cta}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Fleet Section - con animazioni
const FleetSection = ({ t, lang, onVehicleClick }) => {
  const vehicles = getVehicles(lang);

  return (
    <section id="veicoli" className="py-24 bg-slate-900" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block bg-slate-800 text-cyan-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            {t.fleet.badge}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">{t.fleet.title}</h2>
          <p className="text-slate-400">{t.fleet.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {vehicles.map((vehicle, index) => (
            <div 
              key={vehicle.id} 
              className={`bg-slate-800/50 rounded-3xl overflow-hidden group transition-all elegant-card ${vehicle.premium ? 'premium-glow' : ''}`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="relative h-56 overflow-hidden img-zoom">
                <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                {/* Logo Watermark */}
                <img src={LOGO_URL} alt="" className="absolute bottom-3 right-3 h-7 w-auto opacity-50 z-10" />
                {vehicle.premium && (
                  <span className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                    ✦ PREMIUM
                  </span>
                )}
                <span className="absolute bottom-4 left-4 bg-cyan-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                  {t.fleet.upTo} {vehicle.capacity} {t.fleet.passengers}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">{vehicle.name}</h3>
                <ul className="space-y-2">
                  {vehicle.models.map((model, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-400 text-sm group-hover:text-slate-300 transition-colors stagger-item">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                      {model}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-2">{t.fleet.explore}</h3>
          <p className="text-slate-400">{t.fleet.exploreDesc}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {galleryVehicles.map((vehicle, i) => (
            <div 
              key={i} 
              className="group relative rounded-xl overflow-hidden cursor-pointer hover-lift card-shine"
              onClick={() => onVehicleClick(vehicle.name)}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <img src={vehicle.image} alt={vehicle.name} className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500" />
              {/* Logo Watermark */}
              <img src={LOGO_URL} alt="" className="absolute bottom-2 right-2 h-5 w-auto opacity-50 z-10" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-end pb-3">
                <span className="text-white text-xs font-medium mb-1">{vehicle.name}</span>
                <span className="text-cyan-400 text-xs">{t.fleet.details}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section con animazioni
const FAQSection = ({ t, lang }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqData = getFaqData(lang);

  return (
    <section className="py-24 bg-slate-800" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block bg-slate-700 text-cyan-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            {t.faq.badge}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            {t.faq.title} <span className="text-cyan-400">{t.faq.titleHighlight}</span>
          </h2>
          <p className="text-slate-400 mt-4">{t.faq.subtitle}</p>
        </div>

        <div className="space-y-4">
          {faqData.map((item, i) => (
            <div key={i} className="bg-slate-700/50 rounded-2xl overflow-hidden hover:bg-slate-700/70 transition-colors">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full p-6 text-left flex items-center justify-between">
                <span className="text-white font-medium pr-4">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform duration-300 flex-shrink-0 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-6">
                  <p className="text-slate-400">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-400 mb-4">{t.faq.notFound}</p>
          <a
            href="mailto:info@alexncc.com?subject=Domanda%20sul%20servizio%20NCC"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-6 py-3 rounded-full font-medium transition-all hover:scale-105 shadow-lg shadow-cyan-500/20"
          >
            <Mail className="w-5 h-5" />
            {t.faq.contactBtn}
          </a>
        </div>
      </div>
    </section>
  );
};

// Summer Destinations Section - Mete Estive
const SUMMER_IMAGES = [
  "https://images.unsplash.com/photo-1589340819076-7d3490f70ca1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1558271736-cd043ef2e855?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1592396355679-1e2a094e8bf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1604998103924-89e012e5265a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];
const SUMMER_ICONS = [Waves, MapPin, Anchor, Mountain];
const SUMMER_CHOICES = [
  [
    { id: 'lago_como', label: 'Lago di Como', lat: 45.8081, lng: 9.0852 },
    { id: 'lago_maggiore', label: 'Lago Maggiore', lat: 45.8833, lng: 8.5333 },
    { id: 'lago_orta', label: "Lago d'Orta", lat: 45.7969, lng: 8.4172 },
  ],
  [
    { id: 'venezia', label: 'Venezia', lat: 45.4408, lng: 12.3155 },
    { id: 'verona', label: 'Verona', lat: 45.4384, lng: 10.9916 },
    { id: 'venezia_verona', toId: 'venezia', label: 'Venezia + tappa Verona', lat: 45.4408, lng: 12.3155, note: 'Con tappa a Verona lungo il tragitto' },
  ],
  [
    { id: 'genova', label: 'Genova', lat: 44.4056, lng: 8.9463 },
    { id: 'cinque_terre', label: 'Cinque Terre', lat: 44.0997, lng: 9.7375 },
    { id: 'portofino', label: 'Portofino', lat: 44.3033, lng: 9.2097 },
  ],
  [
    { id: 'courmayeur', label: 'Courmayeur', lat: 45.7967, lng: 6.9686 },
    { id: 'cervinia', label: 'Cervinia', lat: 45.9347, lng: 7.6319 },
    { id: 'monte_bianco', label: 'Monte Bianco', lat: 45.8326, lng: 6.8652 },
  ],
];

const SummerSection = ({ t, onQuoteClick, onDestinationClick }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
  <section id="estate" className="py-16 sm:py-20 md:py-24 bg-slate-900 relative overflow-hidden" data-testid="summer-section">
    {/* Ambient glows */}
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <div className="absolute top-10 left-1/4 w-72 h-72 bg-cyan-400 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-10 right-1/4 w-72 h-72 bg-amber-500 rounded-full filter blur-3xl"></div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
      <div className="text-center mb-12 sm:mb-16">
        <span className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-amber-500/20 border border-amber-500/30 text-amber-300 px-5 py-2 rounded-full text-sm font-medium mb-4">
          <Sun className="w-4 h-4" />
          {t.summer.badge}
        </span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          <span className="gradient-text">{t.summer.title}</span>
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">{t.summer.subtitle}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        {t.summer.destinations.map((d, i) => {
          const Icon = SUMMER_ICONS[i] || MapPin;
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="group relative rounded-2xl sm:rounded-3xl overflow-hidden elegant-card border border-slate-700/50 hover:border-amber-500/40 transition-all cursor-pointer"
              data-testid={`summer-destination-${i}`}
            >
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <img src={SUMMER_IMAGES[i]} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                {/* Logo Watermark */}
                <img src={LOGO_URL} alt="" className="absolute bottom-3 right-3 h-7 w-auto opacity-50 z-10" />
                <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-amber-500 flex items-center justify-center shadow-lg">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="absolute bottom-4 left-4 text-xl sm:text-2xl font-bold text-white drop-shadow-lg">{d.name}</h3>
                {/* Hint: tocca per scegliere */}
                <span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 text-[11px] sm:text-xs bg-slate-900/70 text-cyan-200 px-2.5 py-1 rounded-full border border-cyan-500/30 backdrop-blur-sm">
                  {t.summer.chooseLabel}
                  <ChevronRight className="w-3 h-3" />
                </span>
              </div>
              <div className="p-5 sm:p-6 bg-slate-800/60 backdrop-blur-sm">
                <p className="text-slate-300 text-sm sm:text-base">{d.desc}</p>
              </div>

              {/* Overlay scelte - appare solo al click sulla card */}
              {isOpen && (
                <div
                  className="absolute inset-0 z-30 bg-slate-900/95 backdrop-blur-sm flex flex-col items-center justify-center gap-2.5 p-5 animate-[fadeIn_0.2s_ease-out]"
                  data-testid={`summer-choices-${i}`}
                >
                  <p className="text-cyan-300 text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Icon className="w-4 h-4" />
                    {t.summer.chooseLabel}
                  </p>
                  {(SUMMER_CHOICES[i] || []).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDestinationClick(c); setOpenIndex(null); }}
                      data-testid={`summer-choice-${c.id}`}
                      className="w-full max-w-[220px] text-sm px-4 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white font-medium transition-all hover:scale-[1.03] shadow-lg shadow-amber-500/20"
                    >
                      {c.label}
                    </button>
                  ))}
                  <span className="text-slate-400 text-[11px] mt-1">{d.name}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-10 sm:mt-12">
        <button
          onClick={onQuoteClick}
          data-testid="summer-cta-button"
          className="group bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all shadow-lg shadow-amber-500/20 hover:scale-105 inline-flex items-center gap-2"
        >
          <Sun className="w-5 h-5" />
          {t.summer.cta}
        </button>
      </div>
    </div>
  </section>
  );
};

// How It Works Section - Come Funziona
const HOWITWORKS_ICONS = [Car, Mail, Check];
const HowItWorksSection = ({ t, onQuoteClick }) => (
  <section id="come-funziona" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden" data-testid="how-it-works-section">
    <div className="absolute inset-0 opacity-10 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-cyan-500 rounded-full filter blur-3xl"></div>
    </div>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
      <div className="text-center mb-12 sm:mb-16">
        <span className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-amber-500/20 border border-cyan-500/30 text-cyan-300 px-5 py-2 rounded-full text-sm font-medium mb-4">
          <Zap className="w-4 h-4" />
          {t.comeFunziona.badge}
        </span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          <span className="gradient-text">{t.comeFunziona.title}</span>
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">{t.comeFunziona.subtitle}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-8 sm:gap-6 md:gap-8">
        {t.comeFunziona.steps.map((s, i) => {
          const Icon = HOWITWORKS_ICONS[i] || Check;
          return (
            <div key={i} className="relative text-center px-2 sm:px-4" data-testid={`how-step-${i}`}>
              {i < t.comeFunziona.steps.length - 1 && (
                <div className="hidden sm:block absolute top-8 sm:top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
              )}
              <div className="relative mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20 mb-5">
                <Icon className="w-7 h-7 sm:w-9 sm:h-9 text-white" />
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-slate-900 border border-cyan-500/50 text-cyan-300 text-sm font-bold flex items-center justify-center">{i + 1}</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{s.title}</h3>
              <p className="text-slate-400 text-sm sm:text-base">{s.desc}</p>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-10 sm:mt-12">
        <button
          onClick={onQuoteClick}
          data-testid="how-cta-button"
          className="group bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all shadow-lg shadow-amber-500/20 hover:scale-105 inline-flex items-center gap-2"
        >
          {t.comeFunziona.cta}
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  </section>
);


// Partnership Section
const PartnershipSection = ({ t }) => (
  <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-cyan-900/30 relative overflow-hidden" data-testid="partnership-section">
    {/* Background decoration */}
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-10 right-1/4 w-72 h-72 bg-cyan-400 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-10 left-1/4 w-64 h-64 bg-amber-500 rounded-full filter blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
      <div className="text-center mb-12 sm:mb-16">
        <span className="inline-block bg-gradient-to-r from-cyan-500/20 to-amber-500/20 border border-cyan-500/30 text-cyan-400 px-5 py-2 rounded-full text-sm font-medium mb-4">
          {t.partnership.badge}
        </span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">{t.partnership.title}</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-10 sm:mb-12">
        <div className="bg-gradient-to-br from-cyan-500/10 via-slate-800/80 to-slate-800/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-cyan-500/20 hover:border-cyan-500/40 transition-all hover:shadow-lg hover:shadow-cyan-500/10">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center mb-4 sm:mb-6">
            <Star className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{t.partnership.hotel.title}</h3>
          <p className="text-slate-400 mb-5 sm:mb-6 text-sm sm:text-base">{t.partnership.hotel.desc}</p>
          <ul className="space-y-2 sm:space-y-3">
            {t.partnership.hotel.features.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300 text-sm sm:text-base">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 via-slate-800/80 to-slate-800/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-500/20 hover:border-amber-500/40 transition-all hover:shadow-lg hover:shadow-amber-500/10">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 sm:mb-6">
            <Users className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{t.partnership.agency.title}</h3>
          <p className="text-slate-400 mb-5 sm:mb-6 text-sm sm:text-base">{t.partnership.agency.desc}</p>
          <ul className="space-y-2 sm:space-y-3">
            {t.partnership.agency.features.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300 text-sm sm:text-base">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center border border-slate-700/50 hover:border-cyan-500/30 transition-all">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{t.partnership.cta}</h3>
        <p className="text-slate-400 mb-5 sm:mb-6 max-w-2xl mx-auto text-sm sm:text-base">{t.partnership.ctaDesc}</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <a href="mailto:info@alexncc.com?subject=Richiesta%20Partnership%20B2B" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-6 py-3 rounded-full font-medium transition-all hover:scale-105 shadow-lg shadow-cyan-500/20 text-sm sm:text-base">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
            {t.partnership.email}
          </a>
          <a href="tel:+393400859327" className="inline-flex items-center justify-center gap-2 bg-slate-700/80 hover:bg-slate-600 text-white px-6 py-3 rounded-full font-medium transition-all hover:scale-105 text-sm sm:text-base">
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
            {t.partnership.call}
          </a>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section - Design con urgenza e numeri animati
const CTASection = ({ t, onQuoteClick }) => (
  <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-slate-800 via-slate-900 to-slate-900 relative overflow-hidden" data-testid="cta-section">
    {/* Background decoration */}
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-0 left-1/4 w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
      {/* Urgency badge */}
      <div className="text-center mb-6 sm:mb-8">
        <span className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 text-amber-300 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium animate-pulse">
          <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
          Disponibilità 24/7 — Prenota ora!
        </span>
      </div>

      <div className="text-center mb-8 sm:mb-10">
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-1 sm:mb-2">{t.cta.title || "Non Rimandare,"}</h2>
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4 sm:mb-6">{t.cta.titleHighlight || "Prenota Ora"}</h2>
        <p className="text-slate-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto px-4">{t.cta.subtitle}</p>
      </div>

      <div className="text-center mb-8 sm:mb-10">
        <button 
          onClick={onQuoteClick} 
          className="group bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-5 rounded-full font-bold text-base sm:text-lg md:text-xl transition-all shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105 inline-flex items-center gap-2 sm:gap-3" 
          data-testid="cta-quote-button"
        >
          {t.cta.button}
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Trust features */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-4 md:gap-6 mb-10 sm:mb-12 md:mb-16 px-2">
        {t.cta.features.map((feature, i) => (
          <div key={i} className="flex items-center gap-1.5 sm:gap-2 bg-slate-800/50 backdrop-blur-sm px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 rounded-full border border-slate-700/50">
            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />
            <span className="text-white font-medium text-xs sm:text-sm">{feature}</span>
          </div>
        ))}
      </div>

      {/* Stats grid - Numeri animati */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/30 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-700/30">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
            <AnimatedNumber value={2500} suffix="+" duration={2000} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.cta.stats.clients}</div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/30 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-700/30">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
            <AnimatedNumber value={6} duration={1000} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.cta.stats.vehicles}</div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/30 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-700/30">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">24/7</div>
          <div className="text-slate-400 text-xs sm:text-sm">{t.cta.stats.available}</div>
        </div>
        <div className="text-center p-4 sm:p-5 md:p-6 bg-slate-800/30 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-700/30">
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
            <AnimatedNumber value={4.9} duration={1500} />
          </div>
          <div className="text-slate-400 text-xs sm:text-sm flex items-center justify-center gap-0.5 sm:gap-1 flex-wrap">
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
            <span className="ml-1">{t.cta.stats.rating}</span>
          </div>
        </div>
      </div>

      {/* Testimonials carousel */}
      <div className="mt-10 sm:mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
        {(t.testimonials?.items || [
          { quote: "Matrimonio da sogno! Maserati per noi sposi e minibus per gli invitati, tutto curato nei minimi dettagli. Servizio impeccabile, ospiti entusiasti.", name: "Giulia & Marco", role: "Sposi, Giugno 2025" },
          { quote: "Affidabili e sempre puntuali per i miei spostamenti di lavoro verso gli aeroporti. Auto impeccabili e autisti professionali. Un partner su cui contare.", name: "Stefano R.", role: "Imprenditore, Varese" },
          { quote: "Transfer per Malpensa alle 4 del mattino senza il minimo stress: autista già pronto ad attendermi. Viaggio comodo e rilassante, cinque stelle.", name: "Federica B.", role: "Manager, Como" },
          { quote: "Giornata sul Lago di Como con tappa a Bellagio: organizzazione perfetta, mezzo comodissimo e autista gentilissimo. Esperienza estiva indimenticabile!", name: "Roberto e Anna", role: "Turisti, Agosto 2025" },
        ]).map((testimonial, i) => (
          <div key={i} className="bg-slate-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
            <div className="flex gap-0.5 sm:gap-1 mb-3 sm:mb-4">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <p className="text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4 italic">"{testimonial.quote}"</p>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-cyan-500 to-amber-500 flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">
                {testimonial.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="min-w-0">
                <div className="text-white font-medium text-xs sm:text-sm truncate">{testimonial.name}</div>
                <div className="text-slate-500 text-xs truncate">{testimonial.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Footer (ottimizzato mobile)
const Footer = ({ t }) => (
  <footer className="py-8 sm:py-10 md:py-12 bg-slate-900 border-t border-slate-800" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
        <div className="text-center md:text-left">
          <h3 className="text-white font-bold mb-1 sm:mb-2 text-sm sm:text-base">{t.footer.quickQuote}</h3>
          <p className="text-slate-400 text-xs sm:text-sm">{t.footer.quickQuoteDesc}</p>
        </div>
        <a href="mailto:prenotazioni.alexncc@gmail.com?subject=Richiesta%20Informazioni%20NCC" className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all hover:scale-105 btn-ripple text-sm sm:text-base">
          <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
          {t.footer.contactBtn}
        </a>
      </div>

      {/* Dati aziendali */}
      <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-slate-800">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-center sm:text-left">
          <div className="sm:col-span-2 md:col-span-1">
            <img src={LOGO_URL} alt="Alex Tours Transfer" className="h-10 sm:h-12 w-auto object-contain mx-auto sm:mx-0 mb-3 sm:mb-4" />
            <p className="text-slate-400 text-xs sm:text-sm">{t.footer.description}</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-2 sm:mb-3 text-sm sm:text-base">{t.footer.contacts}</h4>
            <div className="mb-3 p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <p className="text-cyan-400 font-semibold text-xs sm:text-sm">{t.footer.bookingLabel}</p>
              <p className="text-white text-xs sm:text-sm font-medium break-all">📅 prenotazioni.alexncc@gmail.com</p>
            </div>
            <p className="text-slate-400 text-xs sm:text-sm mb-1">📧 info@alexncc.com</p>
            <p className="text-slate-400 text-xs sm:text-sm mb-1">📞 +39 0331 694870</p>
            <p className="text-slate-400 text-xs sm:text-sm">🌐 www.alexncc.com</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-2 sm:mb-3 text-sm sm:text-base">{t.footer.legalOffice}</h4>
            <p className="text-slate-400 text-xs sm:text-sm">Via Torretta 48</p>
            <p className="text-slate-400 text-xs sm:text-sm">21029 Vergiate (VA)</p>
            <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2">Italia</p>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-800 flex flex-col items-center gap-3 sm:gap-4 md:flex-row md:justify-between">
        <p className="text-slate-500 text-xs sm:text-sm text-center md:text-left">
          <span className="font-semibold text-slate-400">Alex Tours Transfer S.r.l.</span> — P.IVA 04011130129
        </p>
        <div className="flex gap-4 text-slate-500 text-[10px] sm:text-xs">
          <button onClick={() => document.getElementById('privacy-modal').showModal()} className="hover:text-cyan-400 transition-colors">Privacy Policy</button>
          <span>|</span>
          <button onClick={() => document.getElementById('cookie-modal').showModal()} className="hover:text-cyan-400 transition-colors">Cookie Policy</button>
        </div>
        <p className="text-slate-500 text-[10px] sm:text-xs order-last md:order-none">© {new Date().getFullYear()} Tutti i diritti riservati</p>
      </div>
      
      {/* SEO Keywords - Zone servite (sr-only = visibile solo ai motori di ricerca) */}
      <div className="sr-only" aria-hidden="true">
        <h2>NCC Malpensa - Taxi Privato Malpensa - Transfer Aeroporto Malpensa</h2>
        <p>Servizio NCC e Taxi Privato per transfer aeroporto Malpensa. Il miglior servizio di noleggio con conducente da e per Malpensa.</p>
        
        <h3>Transfer Malpensa - Destinazioni Principali</h3>
        <ul>
          <li>Transfer Malpensa Milano - NCC Malpensa Milano - Taxi Malpensa Milano</li>
          <li>Transfer Malpensa Varese - NCC Malpensa Varese - Taxi Malpensa Varese</li>
          <li>Transfer Malpensa Como - NCC Malpensa Como - Taxi Malpensa Como</li>
          <li>Transfer Malpensa Torino - NCC Malpensa Torino - Taxi Malpensa Torino</li>
          <li>Transfer Malpensa Aosta - NCC Malpensa Aosta - Taxi Malpensa Aosta</li>
          <li>Transfer Malpensa Valle d'Aosta - NCC Malpensa Courmayeur - Taxi Malpensa Cervinia</li>
          <li>Transfer Malpensa Genova - NCC Malpensa Genova - Taxi Malpensa Genova</li>
          <li>Transfer Malpensa Lago Maggiore - NCC Malpensa Stresa - Taxi Malpensa Arona</li>
          <li>Transfer Malpensa Lugano - NCC Malpensa Svizzera - Taxi Malpensa Lugano</li>
        </ul>

        <h3>Zone Servite - Provincia di Varese</h3>
        <ul>
          <li>NCC Varese Malpensa - Taxi Varese Malpensa - Transfer Varese Malpensa</li>
          <li>NCC Busto Arsizio Malpensa - Taxi Busto Arsizio - Transfer Busto Arsizio Malpensa</li>
          <li>NCC Gallarate Malpensa - Taxi Gallarate - Transfer Gallarate Malpensa</li>
          <li>NCC Vergiate Malpensa - Taxi Vergiate - Transfer Vergiate Malpensa</li>
          <li>NCC Somma Lombardo Malpensa - Taxi Somma Lombardo - Transfer Somma Lombardo</li>
          <li>NCC Saronno Malpensa - Taxi Saronno - Transfer Saronno Malpensa</li>
          <li>NCC Castellanza - Taxi Castellanza - Transfer Castellanza Malpensa</li>
          <li>NCC Legnano Malpensa - Taxi Legnano - Transfer Legnano Malpensa</li>
          <li>NCC Sesto Calende - Taxi Sesto Calende - Transfer Sesto Calende</li>
          <li>NCC Arona Malpensa - Taxi Arona - Transfer Arona Malpensa</li>
          <li>NCC Laveno - Taxi Laveno Mombello - Transfer Laveno Malpensa</li>
          <li>NCC Luino - Taxi Luino - Transfer Luino Malpensa</li>
          <li>NCC Cardano al Campo - Taxi Cardano al Campo - NCC Ferno - NCC Lonate Pozzolo</li>
        </ul>
        
        <h3>Zone Servite - Milano e Como</h3>
        <ul>
          <li>NCC Milano Malpensa - Taxi Milano Malpensa - Transfer Milano Malpensa</li>
          <li>NCC Linate - Taxi Linate - Transfer Aeroporto Linate</li>
          <li>NCC Como Malpensa - Taxi Como Malpensa - Transfer Como Malpensa</li>
          <li>NCC Fiera Milano Malpensa - Transfer Fiera Rho Malpensa</li>
          <li>NCC Monza Malpensa - Taxi Monza - Transfer Monza Malpensa</li>
        </ul>
        
        <h3>Zone Servite - Valle d'Aosta e Piemonte</h3>
        <ul>
          <li>NCC Aosta Malpensa - Taxi Aosta Malpensa - Transfer Aosta Malpensa</li>
          <li>NCC Valle d'Aosta Malpensa - Transfer Valle d'Aosta Malpensa</li>
          <li>NCC Courmayeur Malpensa - Taxi Courmayeur - Transfer Courmayeur Malpensa</li>
          <li>NCC Cervinia Malpensa - Taxi Cervinia - Transfer Cervinia Malpensa</li>
          <li>NCC Torino Malpensa - Taxi Torino Malpensa - Transfer Torino Malpensa</li>
          <li>NCC Torino Caselle - Transfer Torino Caselle Milano</li>
        </ul>
        
        <h3>Servizi Transfer Aeroporto Malpensa</h3>
        <p>
          Transfer Malpensa Terminal 1 - Transfer Malpensa Terminal 2 -
          Taxi Aeroporto Malpensa - Navetta Malpensa privata - Shuttle Malpensa VIP -
          Car Service Malpensa - Limousine Service Malpensa - 
          Noleggio con conducente Malpensa - NCC aeroporto Malpensa -
          Transfer business Malpensa - Transfer eventi Malpensa - Transfer fiere Malpensa -
          Prenotazione taxi Malpensa - Servizio navetta Malpensa Milano -
          Transfer Malpensa prezzo fisso - NCC Malpensa low cost - Taxi privato Malpensa
        </p>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  const [lang, setLang] = useState('IT');
  const [showCookie, setShowCookie] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bookingPreset, setBookingPreset] = useState(null);
  const t = getT(lang);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) setShowCookie(false);
  }, []);

  const handleQuoteClick = () => {
    setBookingPreset(null);
    setShowBookingModal(true);
  };

  const handleDestinationClick = (dest) => {
    setBookingPreset(dest);
    setShowBookingModal(true);
  };

  const handleVehicleClick = (vehicleName) => {
    setSelectedVehicle(vehicleName);
  };

  const handleCookieAccept = () => { localStorage.setItem('cookieConsent', 'accepted'); setShowCookie(false); };
  const handleCookieReject = () => { localStorage.setItem('cookieConsent', 'rejected'); setShowCookie(false); };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header lang={lang} setLang={setLang} t={t} onBookClick={handleQuoteClick} />
      <EmailButton />
      
      <main>
        <HeroSection t={t} onQuoteClick={handleQuoteClick} />
        <AboutSection t={t} />
        <ServicesSection t={t} />
        <HowItWorksSection t={t} onQuoteClick={handleQuoteClick} />
        <SummerSection t={t} onQuoteClick={handleQuoteClick} onDestinationClick={handleDestinationClick} />
        <WeddingSection t={t} onQuoteClick={handleQuoteClick} />
        <FleetSection t={t} lang={lang} onVehicleClick={handleVehicleClick} />
        <FAQSection t={t} lang={lang} />
        <PartnershipSection t={t} />
        <CTASection t={t} onQuoteClick={handleQuoteClick} />
      </main>

      <Footer t={t} />
      {showCookie && <CookieBanner t={t} onAccept={handleCookieAccept} onReject={handleCookieReject} />}
      <BookingModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} lang={lang} presetTo={bookingPreset} />
      <VehicleDetailModal isOpen={!!selectedVehicle} onClose={() => setSelectedVehicle(null)} vehicleName={selectedVehicle} />
      
      {/* Privacy Policy Modal */}
      <dialog id="privacy-modal" className="bg-slate-800 text-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto backdrop:bg-black/70">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Privacy Policy</h2>
          <button onClick={() => document.getElementById('privacy-modal').close()} className="text-slate-400 hover:text-white text-2xl">&times;</button>
        </div>
        <div className="text-slate-300 text-sm space-y-4">
          <p><strong>Titolare del Trattamento:</strong> Alex Tours Transfer S.r.l., Via Torretta 48, 21029 Vergiate (VA), P.IVA 04011130129</p>
          <p><strong>Email:</strong> info@alexncc.com</p>
          
          <h3 className="text-white font-semibold mt-4">1. Dati Raccolti</h3>
          <p>Raccogliamo i seguenti dati personali quando utilizzi il nostro servizio di prenotazione:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Nome e cognome</li>
            <li>Numero di telefono</li>
            <li>Indirizzo email</li>
            <li>Indirizzi di partenza e destinazione</li>
          </ul>
          
          <h3 className="text-white font-semibold mt-4">2. Finalità del Trattamento</h3>
          <p>I tuoi dati vengono utilizzati esclusivamente per:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Gestire le richieste di preventivo e prenotazione</li>
            <li>Contattarti per confermare il servizio</li>
            <li>Fornire il servizio di trasporto richiesto</li>
          </ul>
          
          <h3 className="text-white font-semibold mt-4">3. Base Giuridica</h3>
          <p>Il trattamento è basato sul tuo consenso e sulla necessità di eseguire il contratto di servizio.</p>
          
          <h3 className="text-white font-semibold mt-4">4. Conservazione dei Dati</h3>
          <p>I dati vengono conservati per il tempo necessario a fornire il servizio e per adempiere agli obblighi di legge.</p>
          
          <h3 className="text-white font-semibold mt-4">5. I Tuoi Diritti</h3>
          <p>Ai sensi del GDPR (Reg. UE 2016/679), hai diritto di:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Accedere ai tuoi dati personali</li>
            <li>Richiedere la rettifica o cancellazione</li>
            <li>Opporti al trattamento</li>
            <li>Richiedere la portabilità dei dati</li>
          </ul>
          <p className="mt-2">Per esercitare questi diritti, contattaci a: info@alexncc.com</p>
          
          <h3 className="text-white font-semibold mt-4">6. Sicurezza</h3>
          <p>Adottiamo misure tecniche e organizzative per proteggere i tuoi dati da accessi non autorizzati.</p>
          
          <p className="mt-4 text-slate-400 text-xs">Ultimo aggiornamento: Dicembre 2025</p>
        </div>
        <button onClick={() => document.getElementById('privacy-modal').close()} className="mt-6 w-full py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg font-semibold transition-colors">Chiudi</button>
      </dialog>
      
      {/* Cookie Policy Modal */}
      <dialog id="cookie-modal" className="bg-slate-800 text-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto backdrop:bg-black/70">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Cookie Policy</h2>
          <button onClick={() => document.getElementById('cookie-modal').close()} className="text-slate-400 hover:text-white text-2xl">&times;</button>
        </div>
        <div className="text-slate-300 text-sm space-y-4">
          <p><strong>Titolare:</strong> Alex Tours Transfer S.r.l., Via Torretta 48, 21029 Vergiate (VA)</p>
          
          <h3 className="text-white font-semibold mt-4">Cosa sono i Cookie?</h3>
          <p>I cookie sono piccoli file di testo che vengono salvati sul tuo dispositivo quando visiti un sito web.</p>
          
          <h3 className="text-white font-semibold mt-4">Cookie Utilizzati</h3>
          
          <h4 className="text-cyan-400 font-medium mt-3">Cookie Tecnici (Necessari)</h4>
          <p>Essenziali per il funzionamento del sito. Non richiedono consenso.</p>
          <ul className="list-disc list-inside ml-4">
            <li>Cookie di sessione</li>
            <li>Cookie per le preferenze di lingua</li>
            <li>Cookie per ricordare la scelta sui cookie</li>
          </ul>
          
          <h4 className="text-cyan-400 font-medium mt-3">Cookie Analitici</h4>
          <p>Utilizziamo strumenti di analisi per capire come gli utenti utilizzano il sito e migliorare l'esperienza.</p>
          
          <h4 className="text-cyan-400 font-medium mt-3">Cookie di Terze Parti</h4>
          <p>Il sito utilizza servizi di terze parti che potrebbero installare cookie:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Google Maps - per la funzionalità di ricerca indirizzi</li>
            <li>Google Analytics - per statistiche anonime</li>
          </ul>
          
          <h3 className="text-white font-semibold mt-4">Come Gestire i Cookie</h3>
          <p>Puoi gestire le preferenze sui cookie:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Attraverso il banner cookie mostrato alla prima visita</li>
            <li>Dalle impostazioni del tuo browser</li>
          </ul>
          
          <h3 className="text-white font-semibold mt-4">Maggiori Informazioni</h3>
          <p>Per informazioni sui cookie di terze parti, consulta le rispettive privacy policy:</p>
          <ul className="list-disc list-inside ml-4">
            <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Google Privacy Policy</a></li>
          </ul>
          
          <p className="mt-4 text-slate-400 text-xs">Ultimo aggiornamento: Dicembre 2025</p>
        </div>
        <button onClick={() => document.getElementById('cookie-modal').close()} className="mt-6 w-full py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg font-semibold transition-colors">Chiudi</button>
      </dialog>
    </div>
  );
}

export default App;
